// en-US dictionary — the key authority. Every user-facing string rendered by shared components,
// the shell and Batch 1 wiring lives here; zh-CN (messages.zh-CN.ts) must define exactly the
// same keys (TypeScript enforces both directions; tests/i18n/dictionaries.test.ts re-checks at
// runtime and pins the frozen glossary). Placeholders use {name}; plural forms end in .one /
// .other and are read through tp().
//
// Controlled vocabulary (Final Implementation Authority §5.1 / §5.4 / §21.4): the only inspector
// results are Pass / Defect / Unable to Inspect / N/A; the non-result state shows as Pending on
// an item pill and Uninspected as a count category. "Issue", "Fail", "Flag", "Acceptable" never
// appear as results.

export const enUS = {
  // ---- navigation identity (route ids never change with language) ----
  'nav.inspections': 'Inspections',
  'nav.areas': 'Areas',
  'nav.findings': 'Findings',
  'nav.sync': 'Sync',

  // ---- shell / chrome ----
  'shell.productName': 'AI Home Inspector',
  'launch.tagline': 'Inspect · Record · Report',
  'launch.skip': 'Skip',
  'shell.back': 'Back',
  'shell.close': 'Close',
  'shell.cancel': 'Cancel',
  'shell.done': 'Done',
  'shell.loading': 'Loading…',
  'shell.screenFailedTitle': 'This page could not open',
  'shell.screenFailedBody': 'Your inspections and photos are still on this device. Go back to Home and open it again from there.',
  'shell.screenFailedHome': 'Back to Home',
  'shell.account': 'Account',
  'shell.appLanguage': 'App Language',
  'shell.reportLanguage': 'Report Language',
  'shell.version': 'Version',
  'shell.signedInAs': 'Signed in as {email}',
  'shell.currentProperty': 'Current Property',
  'shell.progress': 'Progress',
  'shell.noProperty': 'No inspection open',
  'shell.noInspectionTitle': 'Open an inspection first',
  'shell.noInspectionBody': 'Areas and Findings work on the inspection you are working in.',
  'shell.goToInspections': 'Go to Inspections',
  'shell.backToAreas': 'Back to Areas',
  'shell.syncStatus': 'Status',
  'shell.syncBody': 'Inspections, photos and findings are saved on this device as you work.',

  // ---- offline / sync status chips (§13.3 — presentation only) ----
  'status.offlineSaved': 'Offline · Saved',
  'status.offlineLocal': 'Offline · Local',
  'status.localOnly': 'Local only',
  'status.synced': 'Synced',
  'status.syncing': 'Syncing…',
  'status.syncPending': 'Sync Pending ({n})',
  'status.syncFailed': 'Sync Failed',
  'status.savedOnDevice': 'Saved on this device',

  // ---- inspector results (frozen glossary) ----
  'result.pass': 'Pass',
  'result.defect': 'Defect',
  'result.unableToInspect': 'Unable to Inspect',
  'result.na': 'N/A',
  'result.pending': 'Pending',
  'result.uninspected': 'Uninspected',

  // ---- finding vocabulary (frozen glossary) ----
  'finding.finding': 'Finding',
  'finding.findings': 'Findings',
  'finding.title': 'Finding Title',
  'finding.observation': 'Observation',
  'finding.recommendation': 'Recommendation',
  'finding.responsibleTrade': 'Responsible Trade',
  'finding.reportSection': 'Report Section',
  'finding.save': 'Save Finding',
  'finding.savedToReportDraft': 'Saved to Report Draft',
  'finding.reportDraft': 'Report Draft',
  'finding.inspect': 'Inspect',
  'finding.finalReview': 'Final Review',

  // ---- photo actions (frozen glossary) ----
  'photo.takePhoto': 'Take Photo',
  'photo.chooseLibrary': 'Choose Library',
  'photo.libraryShort': 'Library',
  'photo.cameraShort': 'Camera',
  'photo.addPhoto': 'Add Photo',
  'photo.addPhotoNote': 'Add Photo Note',
  'photo.notePrefix': 'Note:',
  'photo.editNote': 'Edit note',
  'photo.open': 'Open photo',
  'photo.removeFromItem': 'Remove from Item',
  'photo.deletePhoto': 'Delete Photo',

  // ---- languages ----
  'language.english': 'English',
  'language.chinese': '中文',
  'language.appLanguageBody': 'App Language changes menus and controls only. Each inspection keeps its own Report Language.',
  'language.reportLanguageDefault': 'Report Language default: English (set per inspection).',

  // ---- inspection shell wiring (Batch 1 minimal) ----
  'inspection.notFoundTitle': 'Inspection not found',
  'inspection.notFoundBody': 'It may not have been created successfully.',
  'inspection.backToInspections': 'Back to Inspections',
  'inspection.noClient': 'No client name',
  // ---- device walkthrough 2026-10-27 (B4 · E1 · E4): AI 单 on the Home plan row, the AI-language note,
  //      and every English count with its singular (E4: "1 Findings") ----
  'home.planAiUnits.one': '{n} AI inspection left',
  'home.planAiUnits.other': '{n} AI inspections left',
  'language.aiResultsNote': 'AI results already produced stay in the language they were written in; tapping “Analyze everything again” in an area rewrites them in the new language and uses AI again.',
  'overview.chipFindings.one': 'Finding',
  'overview.chipFindings.other': 'Findings',
  'home.statInspections.one': 'Inspection',
  'home.statInspections.other': 'Inspections',
  'home.statReports.one': 'Report generated',
  'home.statReports.other': 'Reports generated',
  'home.statFindings.one': 'Finding',
  'home.statFindings.other': 'Findings',
  'common.itemsInspected.one': '{done} of {n} item inspected',
  'common.itemsInspected.other': '{done} of {n} items inspected',
  'overview.areaProgress.one': '{done} of {n} item done',
  'overview.areaProgress.other': '{done} of {n} items done',
  'overview.areaDone.one': 'Complete · {n} item',
  'overview.areaDone.other': 'Complete · {n} items',
  'comments.used.one': 'Used {n} time',
  'comments.used.other': 'Used {n} times',
  'comments.exported.one': '{n} comment exported',
  'comments.exported.other': '{n} comments exported',
  'layout.added.one': 'Added “{name}” with {n} checklist item.',
  'layout.added.other': 'Added “{name}” with {n} checklist items.',
  'area.analyze.one': 'AI analysis ({n} photo)',
  'area.analyze.other': 'AI analysis ({n} photos)',
  'area.completeBlocked.one': '{n} item is still uninspected. Give it a result before completing.',
  'area.completeBlocked.other': '{n} items are still uninspected. Give each one a result before completing.',
  'area.wordingDone.one': 'Report wording written for this area ({n} row).',
  'area.wordingDone.other': 'Report wording written for this area ({n} rows).',
  'area.wordingDoneKept.one': 'Report wording written for this area ({n} row); rows you edited yourself ({kept}) were kept as written.',
  'area.wordingDoneKept.other': 'Report wording written for this area ({n} rows); rows you edited yourself ({kept}) were kept as written.',
  'area.aiNewPhotos.one': 'Analyze {n} new photo',
  'area.aiNewPhotos.other': 'Analyze {n} new photos',
  'area.aiPhase.analyzing.one': 'Analyzing {n} photo…',
  'area.aiPhase.analyzing.other': 'Analyzing {n} photos…',
  'area.aiRedoBody.one': 'The {n} photo of this area is analyzed again and replaces the earlier AI suggestions. This uses AI again (from this inspection; with many photos it may count as one more). To analyze only the photos you added, use “Analyze new photos”.',
  'area.aiRedoBody.other': 'All {n} photos of this area are analyzed again and replace the earlier AI suggestions. This uses AI again (from this inspection; with many photos it may count as one more). To analyze only the photos you added, use “Analyze new photos”.',
  'advanced.willAnalyze.one': 'The {n} photo linked to this item will be analyzed together with:',
  'advanced.willAnalyze.other': 'The {n} photos linked to this item will be analyzed together with:',
  'settings.downloadAllShared.one': '{n} PDF is in the share sheet. Choose Save to Files or save it to a computer.',
  'settings.downloadAllShared.other': '{n} PDFs are in the share sheet. Choose Save to Files or save them to a computer.',
  'settings.downloadAllDownloaded.one': '{n} PDF downloaded.',
  'settings.downloadAllDownloaded.other': '{n} PDFs downloaded.',
  'settings.downloadAllSkipped.one': '{n} report in your account could not be downloaded. Open it from the Reports tab.',
  'settings.downloadAllSkipped.other': '{n} reports in your account could not be downloaded. Open them one by one from the Reports tab.',
  'billing.redeemDone.one': 'Redeemed: {n} inspection added.',
  'billing.redeemDone.other': 'Redeemed: {n} inspections added.',
  'pool.deleteLinkedNote.one': '{n} of them is linked to an item or a finding; its links are removed too.',
  'pool.deleteLinkedNote.other': '{n} of them are linked to items or findings; those links are removed too.',
  'pool.deletedToast.one': 'Deleted {n} photo',
  'pool.deletedToast.other': 'Deleted {n} photos',
  'poolAi.unplaced.one': 'AI could not file {n} photo under a checklist item; it stays in Unclassified for you to link by hand.',
  'poolAi.unplaced.other': 'AI could not file {n} photos under a checklist item; they stay in Unclassified for you to link by hand.',
  'poolAi.retryAreas.one': 'Analyze the {n} unfinished area again',
  'poolAi.retryAreas.other': 'Analyze the {n} unfinished areas again',
  'poolAi.notesMissing.one': '{n} of {total} photos has no note yet.',
  'poolAi.notesMissing.other': '{n} of {total} photos have no note yet.',
  'poolAi.notesHint.one': '{n} photo has no note yet: say where it was taken and what it shows, and the AI analysis gets better.',
  'poolAi.notesHint.other': '{n} photos have no note yet: say where each was taken and what it shows, and the AI analysis gets better.',
  'poolAi.uncertain.one': '{n} photo the AI was not sure about: analyzed in the most likely area but not filed — its observations and the photo show on the items as “AI suggestion · to confirm” for you to confirm or move.',
  'poolAi.uncertain.other': '{n} photos the AI was not sure about: analyzed in the most likely area but not filed — their observations and photos show on the items as “AI suggestion · to confirm” for you to confirm or move.',
  'poolAi.areaFailed.one': '{n} photo · not analyzed: {reason}',
  'poolAi.areaFailed.other': '{n} photos · not analyzed: {reason}',
  'poolAi.doneLine.one': '{n} photo was sorted into {areas}. Open an area to review the AI observations and suggested report wording, then confirm or edit.',
  'poolAi.doneLine.other': '{n} photos were sorted into {areas}. Open an area to review the AI observations and suggested report wording, then confirm or edit.',
  'report.generateSub.one': '{n} PDF · locks the inspection',
  'report.generateSub.other': '{n} PDFs · locks the inspection',
  'report.areasInspected.one': '{n} area inspected',
  'report.areasInspected.other': '{n} areas inspected',
  'report.findingsCount.one': '{n} finding',
  'report.findingsCount.other': '{n} findings',
  'report.limitationsCount.one': '{n} item unable to inspect',
  'report.limitationsCount.other': '{n} items unable to inspect',
  'report.itemResults.one': '{n} item: pass {pass} · defect {defect} · unable to inspect {unable} · n/a {na}',
  'report.itemResults.other': '{n} items: pass {pass} · defect {defect} · unable to inspect {unable} · n/a {na}',
  'report.morePhotos.one': '{n} more photo of this item is not included in this report.',
  'report.morePhotos.other': '{n} more photos of this item are not included in this report.',
  'move.body.one': 'Exports this inspection — {n} photo, the checklist and the agreement — into one file of about {size}. Send it to the other device with AirDrop (iPhone), Quick Share (Android), a cloud drive or a cable, then tap “Import an inspection from a file” there.',
  'move.body.other': 'Exports this inspection — {n} photos, the checklist and the agreement — into one file of about {size}. Send it to the other device with AirDrop (iPhone), Quick Share (Android), a cloud drive or a cable, then tap “Import an inspection from a file” there.',
  'importFile.done.one': 'Imported “{address}” ({n} photo). You can carry on with it on this device.',
  'importFile.done.other': 'Imported “{address}” ({n} photos). You can carry on with it on this device.',
  'drafts.nearBody.one': 'A draft with no final report and no change for 90 days is cleared, with its photos and notes in the app. Photos in your phone library are not affected. The first one is cleared in {n} day: {address}.',
  'drafts.nearBody.other': 'A draft with no final report and no change for 90 days is cleared, with its photos and notes in the app. Photos in your phone library are not affected. The first one is cleared in {n} days: {address}.',
  'inspections.areasDone.one': '{done}/{n} area',
  'inspections.areasDone.other': '{done}/{n} areas',
  'poolAi.analyzing.one': 'Analyzing {areas} ({done} / {n} area done)',
  'poolAi.analyzing.other': 'Analyzing {areas} ({done} / {n} areas done)',
  'poolAi.analyzingShort.one': 'Analyzing area by area ({done} / {n} area done)',
  'poolAi.analyzingShort.other': 'Analyzing area by area ({done} / {n} areas done)',
  'report.itemsInspected.one': '{done} of {n} item with a result',
  'report.itemsInspected.other': '{done} of {n} items with a result',
  'sync.resetLedger.one': 'Old inspection data reset as authorized by the Owner ({date}): {n} record',
  'sync.resetLedger.other': 'Old inspection data reset as authorized by the Owner ({date}): {n} records',
  'inspection.title': 'Inspection',

  // ---- shared controls ----
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.select': 'Select',
  'common.more': 'More',
  'common.dragToReorder': 'Drag to reorder',
  'common.collapse': 'Collapse',
  'common.adjust': 'Adjust',
  'common.decrease': 'Decrease {label}',
  'common.increase': 'Increase {label}',
  'common.remaining': '{n} remaining',
  'common.percent': '{n}%',
  'common.areasCount.one': '{n} Area',
  'common.areasCount.other': '{n} Areas',
  'common.findingsCount.one': '{n} Finding',
  'common.findingsCount.other': '{n} Findings',
  'common.photosCount.one': '{n} Photo',
  'common.photosCount.other': '{n} Photos',
  'common.itemsCount.one': '{n} item',
  'common.itemsCount.other': '{n} items',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.add': 'Add',
  'common.open': 'Open',
  'common.autosaved': 'Saved {time}',
  'common.autosaveHint': 'Changes save automatically.',
  'common.saveFailed': 'Save failed: {message}',

  // ---- enumerations (Round 1: area kinds / groups / floors / presets / statuses / trades) ----
  'areaKind.exterior': 'Exterior & Grounds',
  'areaKind.roof': 'Roof',
  'areaKind.garage': 'Garage',
  'areaKind.basement': 'Basement / Crawlspace',
  'areaKind.structure': 'Structure',
  'areaKind.electrical': 'Electrical',
  'areaKind.plumbing': 'Plumbing',
  'areaKind.waterHeater': 'Water Heater',
  'areaKind.hvac': 'Heating & Cooling',
  'areaKind.attic': 'Attic',
  'areaKind.interior': 'Interior / Living Space',
  'areaKind.kitchen': 'Kitchen',
  'areaKind.bathroom': 'Bathroom',
  'areaKind.bedroom': 'Bedroom',
  'areaKind.safety': 'Safety Review',
  'areaKind.other': 'Other Area',
  'areaGroup.exterior': 'Exterior',
  'areaGroup.spaces': 'Additional Areas',
  'areaGroup.systems': 'Systems',
  'areaGroup.rooms': 'Rooms',
  'areaGroup.wholeHouse': 'Whole House',
  // US usage (Owner report review 2026-09-28): floor 1 = 一楼 = the entry level = "First Floor". The
  // British "Ground Floor / First Floor" made a New York client read 二楼 as the ground level.
  'floor.1': 'First Floor',
  'floor.2': 'Second Floor',
  'floor.3': 'Third Floor',
  'floor.4': 'Fourth Floor',
  'floor.5': 'Fifth Floor',
  'floor.6': 'Sixth Floor',
  'floor.7': 'Seventh Floor',
  'floor.8': 'Eighth Floor',
  'floor.n': 'Level {n}',
  'room.kitchen': 'Kitchen',
  'room.living': 'Living Room',
  'room.family': 'Family Room',
  'room.dining': 'Dining Room',
  'room.bedroom': 'Bedroom',
  'room.fullBath': 'Full Bath',
  'room.fullBathBase': 'Bathroom',
  'room.halfBath': 'Half Bath',
  'room.laundry': 'Laundry Room',
  'space.garage': 'Garage',
  'space.detachedGarage': 'Detached Garage',
  'space.basement': 'Basement',
  'space.crawlspace': 'Crawlspace',
  'space.attic': 'Attic',
  'propertyType.singleFamily': 'Single Family',
  'propertyType.multiFamily': 'Multi-Family',
  'propertyType.condo': 'Condo',
  'propertyType.townhouse': 'Townhouse',
  'propertyType.other': 'Other',
  'inspectionStatus.draft': 'New',
  'inspectionStatus.inProgress': 'In progress',
  'inspectionStatus.completed': 'Completed',
  'findingClass.defect': 'Defect',
  'findingClass.safety': 'Safety',
  'findingClass.maintenance': 'Maintenance',
  'findingClass.advisory': 'Advisory',
  'findingClass.informational': 'Informational',
  'findingStatus.draft': 'Draft',
  'findingStatus.confirmed': 'In Report Draft',
  'findingStatus.rejected': 'Withdrawn',
  'reason.obstructed': 'Obstructed by contents or finishes',
  'reason.inaccessible': 'Inaccessible / Locked',
  'reason.utilitiesOff': 'System not on',
  'reason.unsafeCondition': 'Unsafe condition',
  'reason.notInstalled': 'Not installed',
  'reason.other': 'Other',
  'trade.licensedElectrician': 'Qualified Electrician',
  'trade.licensedPlumbingProfessional': 'Qualified Plumbing Professional',
  'trade.qualifiedHvacContractor': 'Qualified HVAC Contractor',
  'trade.structuralEngineer': 'Professional Engineer (PE)',
  'trade.qualifiedRoofingContractor': 'Qualified Roofing Contractor',
  'trade.licensedPestProfessional': 'Qualified Pest Control / WDI Professional',
  'trade.other': 'Other (specify)',
  'trade.qualifiedGeneralContractor': 'Qualified Contractor',
  'trade.qualifiedMasonryContractor': 'Qualified Mason / Masonry Contractor',
  'trade.qualifiedSidingContractor': 'Qualified Siding Contractor',
  'trade.qualifiedWindowDoorContractor': 'Qualified Window & Door Contractor',
  'trade.qualifiedGarageDoorTechnician': 'Qualified Garage Door Company',
  'trade.qualifiedServiceTechnician': 'Qualified Service Technician',
  'trade.qualifiedChimneyProfessional': 'Qualified Chimney Professional',
  'trade.qualifiedInsulationContractor': 'Qualified Insulation Contractor',
  'trade.qualifiedWaterproofingContractor': 'Qualified Waterproofing Contractor',
  'trade.qualifiedDrainageGradingContractor': 'Qualified Landscaping / Drainage Contractor',
  'trade.qualifiedPavingContractor': 'Qualified Paving / Concrete Contractor',
  'trade.licensedMoldAssessor': 'NYS Licensed Mold Assessor',
  'priority.label': 'Impact / Priority',
  'priority.moderate': 'Moderate',
  'priority.value': 'Priority {n}',

  // ---- M00 Inspections ----
  'inspections.onDevice.one': '{n} inspection on this device',
  'inspections.onDevice.other': '{n} inspections on this device',
  'inspections.current': 'Current Inspection',
  'inspections.resume': 'Resume Inspection',
  'inspections.new': 'New Inspection',
  'inspections.recent': 'Recent History',
  'drafts.nearTitle.one': '{n} draft will be cleared soon',
  'drafts.nearTitle.other': '{n} drafts will be cleared soon',
  'drafts.keep': 'Keep',
  'drafts.clearedTitle.one': '{n} draft was cleared',
  'drafts.clearedTitle.other': '{n} drafts were cleared',
  'drafts.clearedBody': 'These drafts had no change for 90 days and no final report: {list}.',
  'drafts.dismiss': 'OK',
  'drafts.noAddress': 'No address',
  'assist.speak': 'Speak',
  'assist.listening': 'Listening. Tap to stop',
  'assist.switchLang': 'Switch the spoken language',
  'assist.langEn': 'EN',
  'assist.langZh': 'ZH',
  'assist.keyboardMic': 'Tap the microphone on the keyboard to speak. It uses the phone’s own speech recognition.',
  'assist.speechDenied': 'Microphone or speech recognition is not allowed. Turn it on in the phone’s settings, or use the keyboard microphone.',
  'assist.speechNoSpeech': 'No speech was heard. Try again.',
  'assist.speechFailed': 'Speech recognition did not work. Try again or use the keyboard microphone.',
  'assist.library': 'My comments',
  'assist.saveAsComment': 'Save as comment',
  'assist.saved': 'Saved to My comments',
  'assist.exists': 'This comment is already saved',
  'assist.full': 'My comments holds 500 entries. Delete some first.',
  'assist.emptyToSave': 'Write something first',
  'assist.pickerTitle': 'My comments',
  'assist.pickerSubtitle': 'Tap one to add it to what you are writing',
  'assist.search': 'Search comments',
  'assist.pickerEmpty': 'No comments yet. Write something and tap Save as comment, or add them in Settings › My comments.',
  'comments.title': 'My comments',
  'comments.subtitle': 'Your own wording, one tap away',
  'comments.intro': 'Your own reusable wording, kept for your account on this device. Tap My comments under any text field to insert one, or Save as comment after you write.',
  'comments.add': 'Add comment',
  'comments.addPlaceholder': 'e.g. Recommend evaluation and repair by a licensed plumbing professional.',
  'comments.save': 'Save',
  'comments.edit': 'Edit',
  'comments.delete': 'Delete',
  'comments.deleteTitle': 'Delete this comment?',
  'comments.deleteBody': 'Only this entry in My comments is deleted. Text already written into inspections stays.',
  'comments.empty': 'No comments yet.',
  'comments.count': '{n} in total',
  'comments.export': 'Export comments',
  'comments.exportFile': 'My comments',
  'comments.exportFailed': 'Could not export: {detail}',
  'comments.required': 'Write the comment first',
  'annotate.open': 'Mark up',
  'annotate.edit': 'Edit marks',
  'annotate.title': 'Mark up the photo',
  'annotate.subtitle': 'The photo stays as taken; the marks print on it in the report',
  'annotate.tool': 'Mark type',
  'annotate.arrow': 'Arrow',
  'annotate.circle': 'Circle',
  'annotate.box': 'Box',
  'annotate.hint': 'Drag a finger on the photo to draw. An arrow points from where you press to where you lift.',
  'annotate.limit': 'Up to {n} marks per photo',
  'annotate.undo': 'Undo',
  'annotate.clear': 'Clear all',
  'annotate.save': 'Save marks',
  'guide.row': 'Photo checklist · {done} of {total} done',
  'guide.title': 'Photo checklist',
  'guide.subtitle': '{done} of {total} done',
  'guide.intro': 'The photos this kind of area usually has. Take one and it is ticked; if the part is not here or the photo is not needed, say so on the right. It only reminds: it never blocks completing the area or the report. A part marked Not here is passed to the AI so it judges it as missing.',
  'guide.taken': '{n} taken',
  'guide.absent': 'Not here',
  'guide.notNeeded': 'Not needed',
  'guide.open': 'Not taken yet',
  'guide.camera': 'Take photo',
  'guide.library': 'Choose from library',
  'guide.markAbsent': 'Not here',
  'guide.markNotNeeded': 'Not needed',
  'guide.undo': 'Undo',
  'inspections.emptyTitle': 'No inspections yet',
  'inspections.emptyBody': 'Start your first inspection. Everything you record stays on this device.',
  'inspections.options': 'Inspection options',
  'inspections.delete': 'Delete Inspection',
  'inspections.deleteTitle': 'Delete “{address}”?',
  'inspections.deleteBody': 'Removes every record of this inspection from this app (areas, items, findings, photo records). Photos in the system album are not affected. This cannot be undone.',
  // Inspections home (Figma "01 — Inspections Home", iPhone design frozen 2026-09-10)
  'home.brand': 'AI Home Inspector',
  'inspections.clientLabel': 'Client',
  'inspections.scheduledLabel': 'Scheduled',
  'inspections.progressLabel': 'Inspection Progress',
  'inspections.scheduleOrNew': 'Schedule / New Inspection',
  'inspections.clientLine': 'Client: {name}',
  'inspections.finishedOn': 'Finished {date}',
  // Home tab (Owner 2026-09-11: a proper home page)
  'nav.home': 'Home',
  'home.greeting.morning': 'Good morning',
  'home.greeting.noon': 'Good afternoon',
  'home.greeting.afternoon': 'Good afternoon',
  'home.greeting.evening': 'Good evening',
  'home.greetingNamed.morning': 'Good morning, {name}',
  'home.greetingNamed.noon': 'Good afternoon, {name}',
  'home.greetingNamed.afternoon': 'Good afternoon, {name}',
  'home.greetingNamed.evening': 'Good evening, {name}',
  'home.license': '{state} license · {number}',
  'home.licenseMissing': 'License not linked — link it',
  'home.focusEyebrow': 'Pick up where you left off',
  'home.focusEmptyTitle': 'Where do we start today?',
  'home.focusEmptyBody': 'Create an inspection with the address and client, and you are ready to go.',
  'home.quickActions': 'Quick actions',
  'home.allInspections': 'All Inspections',
  'home.today': 'Today',
  'home.todayEmpty': 'Nothing scheduled today',
  'home.upcoming': 'Coming up',
  'home.scheduleHint': 'Set the date and time when you create an inspection and it shows up here.',
  'home.todo': 'To do',
  'home.todoEmpty': 'Nothing to follow up right now',
  'home.todoReady': 'Every item inspected — ready for closeout',
  'home.todoProfileTitle': 'Link your license',
  'home.todoProfile': 'Formal inspections need your linked New York license',
  'home.reportDue': 'Report due by {date} — New York requires delivery within five business days after the inspection (RPL §444-g(3)).',
  'home.reportOverdue': 'Report overdue since {date} — New York requires delivery within five business days after the inspection.',
  'home.month': 'This month',
  'home.footer': 'Works offline · Records stay on this device · Photos stay in your album',
  'home.footer.web': 'Web version · Records stay in this browser · Originals stay on your computer',

  // ---- Property Setup (New Inspection) ----
  'setup.title': 'New Inspection',
  'setup.property': 'Property',
  'setup.address': 'Street Address',
  'setup.addressPlaceholder': 'e.g. 123 Main St',
  'setup.cityPlaceholder': 'e.g. Flushing',
  'setup.city': 'City',
  'setup.state': 'State',
  'setup.zip': 'ZIP',
  'setup.propertyType': 'Property Type',
  'setup.yearBuilt': 'Year Built',
  'setup.weather': 'Weather',
  'setup.weatherPlaceholder': 'e.g. Sunny, 46°F',
  'setup.clientName': 'Client Name',
  'setup.clientNamePlaceholder': 'Client or party ordering the inspection',
  'setup.clientPhone': 'Client Phone',
  'setup.inspector': 'Inspector',
  'setup.inspectorPlaceholder': 'Your name (remembered on this device)',
  'setup.create': 'Create Inspection',
  'setup.createSub.one': '{n} Area · checklist included',
  'setup.createSub.other': '{n} Areas · checklist included',
  'setup.needArea': 'Add at least one area to start inspecting.',
  'setup.hint': 'Areas can be added, disabled or renamed any time in Property Layout.',
  'plan.createsBefore': 'Creates',
  'plan.emptyCreatesBefore': 'No layout yet: only',
  'plan.emptyCreatesAfter': ' whole-house system areas (structure, electrical, plumbing…) are created; setting the layout adds the floors, rooms and spaces.',
  'plan.createsAfter.one': ' area with its checklist; adjust any time after.',
  'plan.createsAfter.other': ' areas, each with its checklist; adjust any time after.',
  'plan.noSpaces': 'No additional spaces',
  'plan.allSystems': 'All {n}',
  'plan.noSystems': 'All off',
  'plan.without': 'Without {list}',
  'plan.noRooms': 'No rooms yet',
  'plan.extraPlaceholder': 'Other room, e.g. Study / Sunroom',
  'plan.remove': 'Remove {name}',
  'plan.floorsCount.one': '{n} floor',
  'plan.floorsCount.other': '{n} floors',
  'plan.bedCount': '{n} bed',
  'plan.bathCount': '{n} bath',
  'plan.kitchenCount': '{n} kitchen',

  // ---- M01 Property Layout ----
  'layout.title': 'Property Layout',
  'layout.hint': 'Arrange only the areas you will actually inspect. Each area loads its checklist automatically.',
  'layout.done': 'Done',
  'layout.addArea': 'Add Area',
  'layout.addFloorOrCustom': 'Add Floor or Custom Area',
  'layout.optionCustom': 'Custom area',
  'layout.dragHandle': 'Drag to reorder "{name}"',
  'layout.deleteBlockedTitle': 'Can’t delete "{name}"',
  'layout.disableInstead': 'Disable This Area',
  'layout.suggestedTitle': 'Suggested Layout',
  'layout.suggestedBody': 'Suggested layout for {type}: {floors} · {areas}.',
  'layout.useSuggested': 'Use Suggested Layout',
  'layout.adjustLater': 'Adjust Later',
  'layout.edit': 'Edit Property Layout',
  'layout.areaOptions': 'Area options',
  'layout.rename': 'Rename',
  'layout.moveToFloor': 'Move to Floor',
  'layout.moveUp': 'Move Up',
  'layout.moveDown': 'Move Down',
  'layout.disable': 'Disable',
  'layout.enable': 'Enable',
  'layout.disabledTag': 'Disabled',
  'layout.deleteTitle': 'Delete “{name}”?',
  'layout.deleteBody': 'This area has no inspection records. It is removed together with its checklist items. This cannot be undone.',
  'layout.deleteBlocked': '“{name}” already has inspection records ({data}) and cannot be deleted. Disable it instead.',
  'layout.addAreaTitle': 'Add Area',
  'layout.areaName': 'Area Name',
  'layout.areaNamePlaceholder': 'Optional · default “{name}”',
  'layout.floor': 'Floor',
  'layout.newFloor': 'New: {floor}',
  'layout.noFloor': 'Not on a floor',
  'layout.addNamed': 'Add “{name}”',
  'layout.addFloorBody': 'A floor exists through its areas. Add the first area of a new floor, or an outbuilding.',
  'layout.optionNewFloor': 'New floor: {floor}',
  'layout.optionOutbuilding': 'Outbuilding (detached garage, shed…)',
  'layout.renameTitle': 'Rename Area',
  'layout.moveTitle': 'Move “{name}” to',
  'layout.presetRooms': 'Rooms',
  'layout.presetSpaces': 'Spaces',
  'layout.presetSystems': 'Systems',
  'layout.presetOther': 'Custom',
  'layout.data.settled.one': '{n} item settled',
  'layout.data.settled.other': '{n} items settled',
  'layout.data.annotated.one': '{n} item with notes or photos',
  'layout.data.annotated.other': '{n} items with notes or photos',
  'layout.data.areaNote': 'area note',
  'layout.data.none': 'No inspection records',
  'layout.allDisabled': 'All areas are disabled. Enable or add areas in Property Layout.',

  // ---- Inspection Overview (Areas tab) + details ----
  'overview.progress': 'Overall Progress',
  'overview.resume': 'Resume Inspection',
  'overview.allInspected': 'Every item has a result',
  'overview.details': 'Inspection Details & Notes',
  'overview.areas': 'Areas',
  'overview.chipUninspected': 'Uninspected',
  'overview.chipUnable': 'Unable to Inspect',
  'overview.reportStatus': 'Report status',
  'overview.photosCount.one': '{n} photo',
  'overview.photosCount.other': '{n} photos',
  'details.title': 'Inspection Details',
  'details.address': 'Address',
  'details.cityStateZip': 'City / State / ZIP',
  'details.typeYear': 'Type / Year Built',
  'details.client': 'Client',
  'details.inspector': 'Inspector',
  'details.weather': 'Weather',
  'details.notes': 'Inspection Notes',
  'details.notesPlaceholder': 'Overall notes: arrival, occupancy, access…',
  'details.status': 'Inspection Status',
  'details.markComplete': 'Mark Inspection Complete',
  'details.completeBlocked': 'Complete every enabled area first.',
  'details.reopen': 'Inspection complete · Reopen to edit',

  // ---- M02 Area Detail ----
  // 2026-10-28 area page
  'area.unclassifiedShow': 'Review',
  'area.unclassifiedHide': 'Hide',
  'area.title': 'Area Detail',
  'area.completedSubtitle': 'Area complete · still editable',
  'area.progressTitle': '{name} Progress',
  'area.photos': 'Area Photos',
  'area.photosEmpty': 'No area photos yet. Take a photo or start a full area scan.',
  'area.aiEyebrow': 'Optional AI Assistance',
  'area.aiNeedsPhotos': 'Add area photos to enable AI',
  'area.skipAi': 'Skip AI / Start Inspecting',
  'area.aiSkipped': 'AI skipped · Analyze later',
  'area.showAi': 'Show AI options',
  'area.items': '{name} Items',
  'area.itemsHint': 'Tap any item to evaluate immediately',
  'area.filterAll': 'All',
  'area.filterLimited': 'Unable / N/A',
  'area.noPending': 'No uninspected items left in this area.',
  'area.noMatch': 'No items match this filter.',
  'area.otherFindingsEmpty': 'Findings outside the checklist go here; they stay in this area.',
  'area.notePlaceholder': 'Overall description, limitations, materials…',
  'area.complete': 'Complete Area',
  'area.reopen': 'Area complete · Reopen to edit',
  'area.blockers.one': '{n} item still uninspected',
  'area.blockers.other': '{n} items still uninspected',
  'area.notFound': 'Area not found',
  'area.notFoundBody': 'It may not have been created.',
  'area.clearResult': 'Clear result',
  'area.clearResultTitle': 'Clear this result?',
  'area.clearResultBody': 'The item returns to Uninspected. Notes, photos and findings are kept.',

  // ---- photos ----
  'photo.albumSaved': 'Saved to album',
  'photo.albumFailed': 'Album save failed',
  'photo.albumUnavailable': 'No album on this platform',
  'photo.albumPending': 'Saving to album…',
  'photo.imported': 'Imported',
  'photo.albumFailedDetail': 'Photo saved in the app, but the album save failed: {detail}',
  'photo.albumUnavailableDetail': 'Photo saved in the app. {detail}',
  'home.webNoticeTitle': 'Web version: this computer’s browser is the only place an open inspection is kept',
  'home.webNoticeBody': 'Do not use a private window or clear this site’s data. An inspection is finished on the device where it was started; to change devices, use “Move to another device” in the Inspections list to export a file. Once the final report is generated, its PDF is kept in your account and opens on your phone too.',
  'home.webNoticeNotKept': 'This browser has not yet promised to keep this site’s data and may clear it when space runs low. Bookmark this site, or use Install in the address bar to add it to this computer, and the browser keeps it; generate each report soon after the inspection.',
  'photo.webNoAlbum': 'Photo added. The web version keeps no album copy — the original stays where you picked it from on this computer.',
  'photo.noteTitle': 'Photo Note',
  'photo.notePlaceholder': 'What this photo shows, where it is, anything to remember.',
  'photo.opening': 'Opening camera…',
  'photo.importing': 'Importing…',
  'photo.viewer': 'Photo viewer',
  'photo.counter': '{index} of {total}',
  'photo.previous': 'Previous',
  'photo.next': 'Next',
  'photo.deleteTitle': 'Delete this photo from the app?',
  'photo.deleteBody': 'Only the app record and its links are removed. The photo in the system album is not deleted.',
  'photo.deleteConfirm': 'Delete from app',
  'photo.sourceCamera': 'Camera',
  'photo.sourceLibrary': 'Library import',
  'photo.albumStatePending': 'Saving to album',
  'photo.albumStateSaved': 'Saved to system album',
  'photo.albumStateFailed': 'Album save failed',
  'photo.albumStateUnavailable': 'No system album on this platform',
  'photo.albumStateNotRequired': 'Original stays in the album (imported)',

  // ---- M03 Item Workspace ----
  'item.metaOf': 'Item {index} of {total} · {area}',
  'item.status': 'Item Status',
  'item.reportWording': 'Report wording (preview)',
  'item.reportWordingNone': 'This result has no sentence of its own: Unable and N/A are worded by their own report blocks.',
  'item.reportWordingWriting': 'The AI is writing…',
  'item.reportWordingWritten': 'The report wording for this row is written.',
  'item.reportWordingKept': 'You edited this row while the AI was writing; your wording was kept.',
  'area.generateWording': 'Write this area’s report wording',
  'area.wordingStarted': 'Writing the report wording for this area’s checked rows…',
  'area.wordingNothing': 'No checked rows in this area yet — nothing to write.',
  'area.wordingAllKept': 'Every checked row here carries wording you edited yourself ({kept}); kept as written.',
  'item.hint': 'What to check',
  'item.scopeSupplemental': 'Supplemental observation — beyond the New York minimum, not a required scope item',
  'item.scopeMethod': 'Method / limitation record',
  'item.alarmTested': 'Tested with the test button',
  'item.alarmTestedHint': 'Unchecked = presence only; the report says operational testing was not performed.',
  // The same recorded fact on the other checks whose sentence depends on it (2026-09-29).
  'item.testedGfci': 'Tested (test button or tester)',
  'item.testedOutlets': 'Tested (representative number)',
  'item.testedWindowsDoors': 'Operated (representative number)',
  'item.testedEgress': 'Window operated',
  'item.testedGarageDoor': 'Auto-reverse tested',
  'item.testedBathExhaust': 'Fan or window operated',
  'item.testedHint': 'Unchecked = observed only; the report does not say it was tested or operated.',
  'item.reason': 'Reason',
  'item.inspectorNote': 'Inspector Note',
  'item.inspectorNoteHint': 'For your own use — it never becomes a finding by itself.',
  'item.notePlaceholder': 'What you saw.',
  'item.unableNotePlaceholder': 'Describe the site condition that prevented the inspection.',
  'item.optionalNote': 'Optional Note',
  'item.naNotePlaceholder': 'Why this item does not apply (optional).',
  'item.evidenceEmpty': 'No evidence photos for this item yet.',
  'item.nextItem': 'Next Item',
  'item.noNext': 'No uninspected items left',
  'item.lastUnchecked': 'This is the last one; give it a result and every item is checked',
  'item.incomplete': 'Incomplete',
  'item.unableIncomplete': 'Unable to Inspect needs a reason.',
  'item.saved': 'Saved on this device',

  // ---- Finding editor ----
  'finding.editTitle': 'Edit Finding',
  'finding.newTitle': 'New Finding',
  'finding.otherTitle': 'Other Finding',
  'finding.titlePlaceholder': 'e.g. Corrosion at main water shutoff valve',
  'finding.observationPlaceholder': 'What was observed and where.',
  'finding.recommendationPlaceholder': 'Recommend… (name the type of professional who should address it)',
  'finding.tradeOther': 'Specify Trade',
  'finding.tradeOtherPlaceholder': 'e.g. Licensed chimney professional',
  'finding.saveSub': '(Saved to Report Draft)',
  'finding.discardTitle': 'Discard changes?',
  'finding.discardBody': 'Unsaved changes to this finding are lost. Photos already taken stay in the area.',
  'finding.discard': 'Discard',
  'finding.keepEditing': 'Keep Editing',
  'finding.draftStatus': 'Local draft · not in the Report Draft yet',
  'finding.savedStatus': 'Saved to the Report Draft on this device',
  'finding.withdraw': 'Withdraw Finding',
  'finding.restore': 'Restore Finding',
  'finding.deleteRecord': 'Delete Finding',
  'finding.deleteTitle': 'Delete this finding?',
  'finding.deleteBody': 'Only the finding is deleted. Linked photos stay in the area.',
  'finding.minimum': 'A title and an observation are required to save.',
  'finding.exportIncomplete': 'Recommendation, responsible trade and report section are needed before the report is exported.',

  // ---- M04 Findings ----
  'findings.empty': 'No findings recorded yet',
  'findings.otherFinding': 'Other finding',
  'findings.drafts.one': '{n} draft not yet saved to the Report Draft',
  'findings.drafts.other': '{n} drafts not yet saved to the Report Draft',

  // ---- M05 Sync ----
  'sync.onDevice': 'On This Device',
  'sync.inspections.one': '{n} inspection',
  'sync.inspections.other': '{n} inspections',
  'sync.notEnabled': 'Cloud sync is not enabled in this build. Everything stays on this device, and field work never waits for a connection.',

  // ---- Mobile Owner Test build (2026-09-04): auth · settings · agreement · import / pool · area · closeout · report ----
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'inspection.noAddress': 'No address entered',
  'common.areasKindsCount.one': '{n} area type in scope',
  'common.areasKindsCount.other': '{n} area types in scope',
  'reason.weather': 'Weather / Site Conditions',
  'details.created': 'Created',
  'item.commentShort': 'Comment',
  'auth.heroTitle': 'Save 30–50% of your time from inspection to report.',
  'auth.heroSubtitle': 'Carry the area-by-area checklist and miss nothing; a photo is a record, and AI can draft the wording. Deliver a professional report the same day — even on your first inspection.',
  'auth.heroTags': 'Works offline · AI optional · Productive on day one',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.confirmPassword': 'Confirm Password',
  'auth.newPassword': 'New Password',
  'auth.code': 'Code from the email',
  'auth.signIn': 'Sign In',
  'auth.createAccount': 'Create Account',
  'auth.createTitle': 'Create your account',
  'auth.createBody': 'Sign in is required before inspecting. A verification email follows.',
  'auth.haveAccount': 'I already have an account',
  'auth.forgot': 'Forgot password?',
  'auth.forgotTitle': 'Reset your password',
  'auth.forgotBody': 'We send a code to your email. Enter it with a new password on the next step.',
  'auth.sendReset': 'Send reset code',
  'auth.resetTitle': 'Set a new password',
  'auth.resetBody': 'Enter the code sent to {email} and your new password.',
  'auth.setPassword': 'Set password and sign in',
  'auth.verifyTitle': 'Verify your email',
  'auth.verifyBody': 'Enter the code we sent to {email}. Check the spam folder if it does not arrive.',
  'auth.verify': 'Verify and sign in',
  'auth.resend': 'Resend the email',
  'auth.backToSignIn': 'Back to sign in',
  'auth.or': 'or',
  'auth.apple': 'Continue with Apple',
  'auth.google': 'Continue with Google',
  'auth.show': 'Show',
  'auth.hide': 'Hide',
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.passwordRules': 'At least {n} characters.',
  'auth.passwordMismatch': 'The two passwords do not match.',
  'auth.notConfigured': 'Sign-in service is not configured in this build. Signing in is not possible.',
  'auth.offlineNoSession': 'You are offline. Signing in needs a connection; a session that was valid before keeps working offline.',
  'auth.requiredNote': 'Sign in is required. Field records are saved on this device and keep working offline once you are signed in.',
  'auth.msg.ok': 'Signed in.',
  'auth.msg.checkEmail': 'Check your email for the verification code.',
  'auth.msg.resetSent': 'Reset code sent. Check your email (and spam).',
  'auth.msg.codeSent': 'If an account uses this address, a sign-in code is on its way. Check your email (and spam).',
  'auth.msg.tooSoon': 'A code was sent a moment ago. Wait a minute before asking for another.',
  // The web version's way in for an account made with Apple or Google in the app (2026-10-20).
  'auth.codeSignIn': 'Sign in with an e-mail code',
  'auth.codeSignInTitle': 'Sign in with a code',
  'auth.codeSignInBody': 'We send a one-time code to your account’s e-mail. This also works for an account you created with Apple or Google in the app.',
  'auth.sendCode': 'Send code',
  'auth.msg.notConfigured': 'Sign-in service is not configured in this build.',
  'auth.msg.invalidCredentials': 'Email or password is incorrect.',
  'auth.msg.emailNotConfirmed': 'This email is not verified yet. Enter the code from the verification email.',
  'auth.msg.emailTaken': 'An account with this email already exists. Sign in or reset the password.',
  'auth.msg.weakPassword': 'The password is too short or too simple.',
  'auth.msg.invalidCode': 'The code is invalid or has expired. Request a new one.',
  'auth.msg.network': 'No connection to the sign-in service. Check the network and try again.',
  'auth.msg.cancelled': 'Cancelled.',
  'auth.msg.notNative': 'Apple and Google sign-in are available in the app. Use email on the web.',
  'auth.msg.googleUnavailable': 'Sign in with Google is not set up for this build yet. Use email for now.',
  'auth.msg.appleUnavailable': 'Sign in with Apple is not available right now. Check that this iPhone is signed in to an Apple Account (Settings › Apple Account). If it is, this build does not have Sign in with Apple enabled yet — use email or Google for now.',
  'auth.msg.unknown': 'Sign-in failed.',
  'settings.groupApp': 'App',
  'settings.groupAccount': 'Account',
  'settings.groupLegal': 'Help & Legal',
  'settings.languageBody': 'Follows the device language until you choose one here.',
  'settings.languageSystem': 'Follow system',
  'settings.languageSystemBody': 'Following the device language: currently {locale}. App Language changes menus and controls only.',
  'settings.account': 'Account & Security',
  'settings.accountBody': 'Sign-in, password, delete account',
  'settings.sync': 'Sync & Storage',
  'settings.syncBody': 'What is on this device; sync status',
  'settings.photos': 'Photos & Camera',
  'settings.photosBody': 'Album rules, photo counts',
  'settings.plan': 'Plan & Subscription',
  'settings.planBody': 'Current plan, inspections left, subscription terms',
  'settings.help': 'Help & Support',
  'settings.helpBody': 'Quick guide, build info',
  'settings.privacy': 'Privacy',
  'settings.privacyBody': 'What stays on the device, what the account is used for',
  'settings.terms': 'Terms',
  'settings.termsBody': 'App terms and the inspection service terms',
  'settings.about': 'About',
  'settings.aboutBody': 'Version, storage, data integrity',
  'settings.signOut': 'Sign Out',
  'settings.signOutTitle': 'Sign out of this device?',
  'settings.signOutBody': 'Inspection records stay on this device. Signing in is required to continue working.',
  'settings.signedInAs': 'Signed in as',
  'settings.offlineContinuation': 'Offline — continuing on the session that was valid the last time this device was online.',
  'settings.changePassword': 'Change password (send reset code)',
  'settings.changePasswordSent': 'A reset code was sent to {email}. Sign out and use “Forgot password” to set the new password.',
  'settings.deleteAccount': 'Delete Account',
  'settings.deleteBody': 'Deletes your sign-in account on the server, together with the report PDFs kept in it. Inspection records on this device are not removed by this. Download all reports first.',
  'settings.deleteTitle': 'Delete this account?',
  'settings.deleteConfirmBody': 'The account will be deleted on the server and you will be signed out. The report PDFs kept in it are deleted, and any inspections and AI allowance left on it are forfeited. This cannot be undone. Records on this device stay. An auto-renewing membership is not cancelled by this. Cancel it first in your App Store or Google Play subscriptions.',
  'settings.downloadAll': 'Download all reports first',
  'settings.downloadAllWorking': 'Preparing…',
  'settings.downloadAllNone': 'There are no reports on this device or in your account.',
  'settings.downloadAllFailed': 'Could not finish: {detail}',
  'settings.deleteConfirm': 'Delete my account',
  'settings.deleteDone': 'Account deleted. You are signed out.',
  'settings.deleteUnauthenticated': 'Sign in again before deleting the account.',
  'settings.deleteUnavailable': 'Account deletion is not available right now (service answered {status}). Nothing was changed.',
  'settings.deleteDenied': 'The deletion request was refused. Nothing was changed.',
  'settings.deleteUnknown': 'The result is unknown (connection lost). Try again later; the account is not removed until confirmed.',
  'settings.photosRuleAlbum': 'A photo taken with the app camera is saved to the system photo library immediately — never waiting for AI, upload or saving.',
  'settings.photosRuleImport': 'Photos chosen from the library are not saved again.',
  'settings.photosRuleDelete': 'Deleting a photo in the app removes the app record only. The photo in the system library is never deleted.',
  'settings.photosRuleNotes': 'Every photo can carry a note; the note line is always shown under the photo.',
  'settings.photosRuleAlbum.web': 'The web version imports photos you pick from this computer and keeps no extra copy; the originals stay where they were.',
  'settings.photosRuleDelete.web': 'Deleting a photo in the web version deletes only this browser’s record — never the original on your computer.',
  'settings.planOptions': 'Plans',
  'settings.planSingle': 'Single report',
  'settings.planMonthly': 'Monthly',
  'settings.planAnnual': 'Annual',
  'settings.planAddon': '3-inspection pack',
  // ---- Plan & Subscription / purchases (Owner decisions 2026-09-11: D-3 add-on for subscribers
  // only · D-4 allowance = 30% of the price, shown as "about N inspections left" · D-5 no plan →
  // one $19.99 purchase per formal inspection · X-2 no carry-over · X-5 Apple's redemption sheet).
  // The four prices are fixed constants in billingPolicy.ts and never appear in a translation.
  'billing.currentPlan': 'Current plan',
  'billing.planNone': 'No active plan',
  'billing.renewsOn': 'Renews {date}',
  'billing.endsOn': 'Ends {date}',
  'billing.remainingInspections.one': '{n} inspection left',
  'billing.remainingInspections.other': '{n} inspections left',
  'billing.remainingNote': 'One inspection is the AI analysis of one job, up to about 400 photos; above 400 photos it counts as two.',
  'billing.singleCredits.one': '{n} single report not used yet',
  'billing.singleCredits.other': '{n} single reports not used yet',
  'billing.trialRemaining.one': '{n} free formal report left',
  'billing.trialRemaining.other': '{n} free formal reports left',
  'billing.noAllowance': 'No AI inspections left.',
  'billing.internalTester': 'Internal test account',
  'billing.internalTesterNote': 'This account is for testing and review; inspections and AI analysis are never charged.',
  'billing.lastSynced': 'Updated {time}',
  'billing.singleBody': 'One formal report, with that job’s AI analysis (up to about 400 photos). Does not renew.',
  'billing.monthlyBody': '20 AI inspections every month, unlimited formal reports; what is left does not carry over.',
  'billing.annualBody': '240 AI inspections for the year (one pool), unlimited formal reports; what is left does not carry over.',
  'billing.addOnBody': '3 more AI inspections, for monthly and annual subscribers. Never expire.',
  'billing.buy': 'Buy in the App Store',
  'billing.buying': 'Opening the App Store…',
  'billing.purchaseDone': 'Purchase complete.',
  'billing.purchasePending': 'Purchase pending — Apple will finish it.',
  'billing.purchaseCancelled': 'Purchase cancelled. Nothing was charged.',
  'billing.purchaseFailed': 'Purchase failed. Nothing was charged.',
  'billing.notConfigured': 'Purchases are not set up in this build.',
  'billing.notConfiguredBody': 'This build was made without the App Store purchase connection, so the buy, redeem and restore buttons below are unavailable. A plan or allowance you already have is not affected.',
  'billing.notEligible': 'The 3-inspection pack is for monthly and annual subscribers.',
  'billing.offline': 'Offline — connect to the network to buy.',
  'billing.notSignedIn': 'Sign in again before buying.',
  'billing.simulated': 'Development store simulation — nothing is charged.',
  'billing.restore': 'Restore purchases',
  'billing.restoreDone': 'Purchases restored.',
  'billing.restoreEmpty': 'Nothing to restore on this Apple ID.',
  'billing.manage': 'Manage subscription',
  'billing.manageBody': 'Opens the App Store subscription settings.',
  'billing.offerCode': 'Redeem offer code',
  'billing.offerCodeBody': 'Opens Apple’s official redemption page. The prices on this screen never change.',
  'billing.offerCodeCheck': 'Check redemption',
  'billing.offerCodeWaiting': 'Finish in Apple’s page, then check the result here.',
  'billing.offerCodeApplied': 'Code applied.',
  'billing.offerCodeNotDetected': 'No new purchase was found. If Apple refused the code, nothing was changed here.',
  'billing.terms': 'Subscription terms',
  'billing.terms1': 'Monthly $99 and annual $999.99 renew automatically through your Apple ID until you cancel.',
  'billing.terms2': 'A subscription includes AI inspections: 20 a month, or 240 for the whole year. One inspection is the AI analysis of one job, up to about 400 photos; above that it counts as two.',
  'billing.terms3': 'Inspections do not carry over: whatever is left when a period ends is gone.',
  'billing.terms4': 'Single reports, 3-inspection packs and inspections given by a redemption code never expire.',
  'billing.terms5': 'Cancel at least 24 hours before a period ends, in the Apple ID settings. The app cannot cancel for you.',
  'billing.terms6': 'Payment is charged to your Apple ID at confirmation. No card details are ever entered in this app.',
  'billing.eula': 'Terms of Use (EULA)',
  'billing.buy.android': 'Buy on Google Play',
  'billing.buying.android': 'Opening Google Play…',
  'billing.purchasePending.android': 'Purchase pending — Google Play will finish it.',
  'billing.notConfiguredBody.android': 'This build was made without the Google Play purchase connection, so the buy, redeem and restore buttons below are unavailable. A plan or allowance you already have is not affected.',
  'billing.restoreEmpty.android': 'Nothing to restore on this Google account.',
  'billing.manageBody.android': 'Opens the Google Play subscription settings.',
  'billing.offerCodeBody.android': 'Opens Google Play’s redeem page. The prices on this screen never change.',
  'billing.offerCodeWaiting.android': 'Finish in Google Play, then check the result here.',
  'billing.offerCodeNotDetected.android': 'No new purchase was found. If Google Play refused the code, nothing was changed here.',
  'billing.terms1.android': 'Monthly $99 and annual $999.99 renew automatically on Google Play through your Google account until you cancel.',
  'billing.terms5.android': 'Cancel any time in Google Play under Payments & subscriptions › Subscriptions; you keep access until the paid period ends. The app cannot cancel for you.',
  'billing.terms6.android': 'Payment is charged to your Google Play account at confirmation. No card details are ever entered in this app.',
  // The web version's own payment lane (item 39): RevenueCat Web Billing (Stripe) on a hosted payment page.
  'billing.buy.web': 'Buy on the payment page',
  'billing.buying.web': 'Opening the payment page…',
  'billing.purchasePending.web': 'Confirming this payment — this page updates within a few seconds to a few minutes.',
  'billing.notConfiguredBody.web': 'This web version was published without its payment connection, so the buy buttons below are unavailable. A plan or purchases made in the app work here as usual.',
  'billing.restoreEmpty.web': 'No purchases on this account yet.',
  'billing.manageBody.web': 'A subscription bought on the web: cancel online or see receipts on the payment service’s page. One bought in the iPhone or Android app is managed in the App Store or Google Play.',
  'billing.terms1.web': 'On the web, monthly $94 and annual $949 (plus your local sales tax, shown separately on the payment page) renew automatically at the end of each period, at the price then in effect, until you cancel.',
  'billing.terms5.web': 'Cancel online any time with Manage subscription on this page — as many steps as subscribing took. You keep access until the paid period ends; no refund for part of a period after a renewal (a charge made in error is refunded if reported within 30 days).',
  'billing.terms6.web': 'Payment is processed by Stripe through RevenueCat; card details never pass through this page or our servers. You get a confirmation e-mail, and an annual subscription is reminded 15–45 days before it renews.',
  'billing.autoRenewConsent': 'I have read and agree to the automatic-renewal terms above: I will be charged each period until I cancel.',
  'billing.autoRenewNeedConsent': 'Tick the automatic-renewal terms first.',
  'billing.consentNotRecorded': 'Your agreement could not be saved, so the payment page was not opened. Please try again.',
  'billing.webPurchaseConfirmed': 'Payment confirmed — your plan is updated.',
  'billing.serverOffline': 'Offline — showing the last allowance this device saw.',
  'billing.serverUnavailable': 'The allowance service returned an error; these numbers may be out of date. Please try again shortly.',
  'billing.serverNotConfigured': 'The allowance service is not set up for this build.',
  'billing.unfunded.one': '{n} inspection created offline could not be matched to a purchase.',
  'billing.unfunded.other': '{n} inspections created offline could not be matched to a purchase.',
  'billing.sheetTitle': 'A plan or a single purchase is needed',
  'billing.sheetBody': 'A formal inspection needs an active plan or one unused single purchase. A Demo inspection is always free.',
  'billing.sheetOffline': 'Offline: without a plan or a purchase a formal inspection cannot be created. A Demo inspection still works.',
  'billing.aiQuotaExhausted': 'No AI inspections left. Subscribers can buy a 3-inspection pack under Settings › Plan & Subscription.',
  'billing.offlineNoCredit': 'You are offline and this account has no plan and no unused single purchase. Buy one once you are back online to create a formal inspection; a demo inspection is unaffected.',
  'billing.countsUnavailable': 'What this account has left cannot be confirmed right now (inspections are counted on the server, which did not give an answer this time). Try again shortly; a demo inspection is unaffected.',
  'billing.addOnCta': 'Buy a 3-inspection pack',
  'billing.freeFirst': 'Your first 3 formal reports are free, with no time limit and no payment method needed.',
  'billing.poolLine': 'This period: {left} of {total} left',
  'billing.extraLine': 'Packs and gifts: {n} left',
  'billing.periodMonthly': '/ month',
  'billing.periodAnnual': '/ year',
  'billing.periodOnce': 'one time',
  'billing.redeemTitle': 'Redemption code',
  'billing.redeemBody': 'Enter the code you received to get free AI inspections, usable in Demo inspections.',
  'billing.redeemPlaceholder': 'e.g. ABCDE-FGHJK',
  'billing.redeemConsent': 'I agree that Demo inspections made with these inspections may be used, anonymised, to measure AI accuracy.',
  'billing.redeemAction': 'Redeem',
  'billing.redeemInvalid': 'That code is not valid. Check it and try again.',
  'billing.redeemExpired': 'That code has expired.',
  'billing.redeemAlready': 'You have already used that code.',
  'billing.redeemUsedUp': 'That code has been used up.',
  'billing.redeemNeedConsent': 'Tick the agreement first to redeem.',
  'billing.redeemFailed': 'The code could not be redeemed right now. Try again later.',
  'billing.planCta': 'See plan & subscription',
  'billing.aiBillingUnavailable': 'The AI allowance could not be checked right now. On-site inspection, photos and notes work as usual.',
  'settings.helpGuide': 'Quick guide',
  'settings.helpStep1': 'New Inspection: property, client, date and time, the inspection agreement, the property layout, and how to start.',
  'settings.helpStep2': 'Start by importing photos you already took into the photo pool, or inspect live on site.',
  'settings.helpStep3': 'In each area: one tap per item — Pass, Defect, Unable to Inspect, N/A. Take a photo, pick from the pool or add a comment only when needed.',
  'settings.helpStep4': 'Defect: title, evidence, observation, recommendation, Save. Problems outside the checklist go under “+ Other issue”.',
  'settings.helpStep5': 'Closeout lists exactly what is still missing. Everything works offline.',
  'settings.helpStep6': 'Report: choose the report language at the end and generate the final PDF; the inspection is locked afterwards.',
  'settings.helpSupport': 'Support',
  'settings.helpSupportBody': 'When something goes wrong, say which screen you were on, the step you took and what you expected — that is what makes it reproducible.',
  'settings.helpBuild': 'Build {version}',
  // (The five Round-2 privacy paragraphs were replaced on 2026-09-09 by the Owner-approved
  //  paragraph `settings.privacyNotice` — approved draft §6.)
  // Two app-side facts the approved paragraph does not cover but the user can see: the album copy
  // and where sharing sends a PDF.
  'settings.privacyAppFacts': 'Photos taken with the app camera are also saved to the device’s system photo library; deleting a photo in the app never deletes the library copy. Sharing a PDF sends it only to the destination you pick in the share sheet.',
  'settings.privacyAppFacts.web': 'In the web version, inspection records and photos are kept only in this computer’s browser; deleting a photo never deletes the original on your computer. A downloaded PDF goes to the browser’s download folder.',
  // 2026-09-25: the full policy lives at nezha2capital.com/privacy-home-inspector (zh + en; the URL registered with the stores). Link only, no copy here.
  'settings.privacyFullPolicy': 'Read the full privacy policy (web, English available)',
  'settings.termsApp': 'App terms',
  'settings.termsFull': 'Read the full Terms of Service (web; the English text governs)',
  'settings.termsApp1': 'The app is a field tool for licensed home inspectors. The inspector makes every inspection decision; the app records, organizes and prints what the inspector decided.',
  'settings.termsApp2': 'AI features only suggest; they never change an inspection result or write into a report without the inspector’s confirmation.',
  'settings.aboutDatabase': 'Database',
  'settings.aboutSchema': 'Data schema',
  'settings.aboutNamespace': 'Storage namespace',
  'settings.aboutIntegrity': 'Data integrity',
  'settings.aboutUpgraded': 'Records upgraded at start',
  'settings.aboutDropped': 'Records skipped at start (unreadable)',
  'settings.aboutIntegrityNote': 'Unreadable records are skipped instead of preventing the app from starting; other records are unaffected.',
  'settings.aboutLegacy': 'AI Home Inspector is a newly built app: your inspection records live on this device, and records from other apps do not appear here.',
  'setup.client': 'Client',
  'setup.clientEmail': 'Client Email',
  'setup.schedule': 'Inspection Date & Inspector',
  'setup.inspectionAt': 'Inspection Date & Time',
  'setup.startTitle': 'How do you want to start?',
  'setup.startBody': 'Both paths keep every capability: AI is optional and the checklist follows the agreement.',
  'setup.startImportTitle': 'Import existing photos',
  'setup.startImportBody': 'Photos already taken go into the photo pool first; organize them with AI or by hand.',
  'setup.startLiveTitle': 'Inspect live on site',
  'setup.startLiveBody': 'Walk the areas, tap results, take photos as you go.',
  'setup.needStartMode': 'Choose how to start.',
  'setup.createImport': 'Create & Import Photos',
  'layout.basementDetails': 'Basement Details',
  'layout.basementBody': 'Space type and finish state are recorded separately.',
  'layout.basementType': 'Space type',
  'layout.basementFinish': 'Finish state',
  'basementType.full': 'Full Basement',
  'basementType.partial': 'Partial Basement',
  'basementType.crawlspace': 'Crawlspace',
  'basementType.slab': 'Slab',
  'basementType.none': 'None',
  'basementFinish.unfinished': 'Unfinished',
  'basementFinish.finished': 'Finished',
  'basementFinish.partial': 'Partially finished',
  'basementFinish.mixed': 'Mixed',
  'basementFinish.unknown': 'Not determined',
  'agreement.title': 'Inspection Agreement',
  'agreement.view': 'View agreement',
  'import.title': 'Import Existing Photos',
  'import.eyebrow': 'Photo pool',
  'import.heading': 'All photos go into the photo pool first',
  'import.body': 'Pick the photos you already took on this property from the library. They are linked to checklist items later — with AI or by hand.',
  'import.noAiNote': 'Importing never depends on AI. Nothing is analysed until you ask.',
  'import.pick': 'Choose photos from the library',
  'import.pickMore': 'Add more photos',
  'import.imported.one': '{n} photo added to the pool.',
  'import.imported.other': '{n} photos added to the pool.',
  'import.empty': 'No photos in the pool yet.',
  'import.continue': 'Continue',
  'import.skip': 'Skip import, go to the inspection',
  'import.choiceTitle': 'How to process the photos',
  'import.inPool.one': '{n} photo in the pool',
  'import.inPool.other': '{n} photos in the pool',
  'import.choiceHeading': 'Your photos are in the pool',
  'import.choiceBody': 'Let AI sort all the photos into areas and checklist items and analyze each area — or skip AI: walk the checklist and pick photos from the pool for each item.',
  'import.reading': 'Reading the selected photos…',
  'import.progress': 'Importing photos {done} / {total}',
  'import.progressHint': 'Many photos take a moment. Keep the app open; you will see when it is done.',
  'import.manualTitle': 'Inspect manually without AI',
  'import.manualBody': 'Walk the checklist and pick photos from the pool for each item; write comments and record findings yourself.',
  'import.aiRules': 'AI never decides an inspection result. It suggests; you confirm.',
  'pool.title': 'Photo Pool',
  'pool.titleShort': 'Pool',
  'pool.open': 'Open the photo pool',
  'pool.photos': 'Photos',
  'pool.select': 'Select',
  'pool.filterAll': 'All',
  'pool.filterUnlinked': 'Unlinked',
  'pool.filterLinked': 'Linked',
  'pool.emptyTitle': 'The photo pool is empty',
  'pool.emptyBody': 'Every photo of this inspection appears here — imported or taken in an area — and can be linked to any item or finding.',
  'pool.linkSelected': 'Link {n} selected',
  'pool.linkToArea': 'Link to which area?',
  'pool.linkToTarget': 'Choose the item or finding',
  'pool.linkedToast': 'Linked {n} to {target}',
  'pool.linkedCount.one': 'Linked to {n} place',
  'pool.linkedCount.other': 'Linked to {n} places',
  'pool.removeLink': 'Remove link',
  'pool.removeLinkTitle': 'Remove this link?',
  'pool.removeLinkBody': 'The photo stays in the pool and in the album. Only the link to {target} is removed.',
  'pool.unlinkedBanner.one': '{n} photo in the pool is not linked yet',
  'pool.unlinkedBanner.other': '{n} photos in the pool are not linked yet',
  'pool.unlinkedBannerBody': 'Link them from any checklist item, or from the pool itself.',
  'pool.pickTitle': 'Choose from the photo pool',
  'pool.pickBody': 'Select photos to link. A photo can be linked to several items or findings; it is never moved or copied.',
  'pool.pickEmpty': 'No photos available to link.',
  'pool.deleteSelected': 'Delete selected ({n})',
  'pool.deleteManyTitle': 'Delete these {n} photos from the app?',
  'poolAi.eyebrow': 'AI PHOTO POOL',
  'poolAi.title': 'Analyze all photos with AI',
  'poolAi.body': 'AI first sorts the pool’s unclassified photos into areas and checklist items, then analyzes each area and drafts observations with suggested report wording. You review and edit every item.',
  'poolAi.start.one': 'Analyze all photos with AI ({n} photo)',
  'poolAi.start.other': 'Analyze all photos with AI ({n} photos)',
  'poolAi.nothing': 'There are no unclassified photos in the pool.',
  'poolAi.sorting': 'Working out which area and item each photo shows… {done} / {total}',
  'poolAi.liveHint': 'Keep the app open — you can look at other pages meanwhile.',
  'poolAi.estimate': 'Estimated time: {duration}. Keep the app open while it runs — you can look at other pages meanwhile.',
  'poolAi.estimateMeasured': 'Estimated from this phone’s earlier runs: {duration}. Keep the app open while it runs — you can look at other pages meanwhile.',
  'poolAi.duration.minutes.one': 'about {n} minute',
  'poolAi.duration.minutes.other': 'about {n} minutes',
  'poolAi.duration.underMinute': 'less than a minute',
  'poolAi.clock.minSec': '{m} min {s} s',
  'poolAi.clock.sec': '{s} s',
  'poolAi.elapsed': '{time} elapsed',
  'poolAi.remaining': '{duration} left',
  'poolAi.overrun': 'taking a little longer than expected — still working',
  'poolAi.doneTitle': 'AI analysis finished',
  'poolAi.doneNone': 'AI could not tell where these photos were taken. They stay in Unclassified for you to link by hand.',
  'poolAi.areaLine': 'Photos: {photos} · items with AI observations: {rows} · to sort: {unclassified}',
  'poolAi.openUnclassified': 'View unclassified photos',
  'poolAi.reviewHint': 'AI never picks an inspection result. Every observation carries suggested report wording: once you set an item’s result, “Write the wording from the AI observation” puts it into the report.',
  'poolAi.failedTitle': 'AI analysis did not finish',
  'poolAi.failedNothingFiled': 'No photo was sorted; you can try again.',
  'poolAi.showResult': 'View AI analysis result',
  'poolAi.notesTitle': 'Add photo notes first?',
  'poolAi.notesBody': 'Adding a note to every photo makes the AI analysis better.',
  'poolAi.notesAddNow': 'Add notes now',
  'poolAi.notesAnalyzeNow': 'Analyze with AI now',
  'poolAi.houseFactsHint': 'The house facts are not recorded yet (heating fuel, water supply, waste disposal, basement). Once they are, the AI stops asking “is this an oil tank?” in every area.',
  'poolAi.houseFactsOpen': 'Record the house facts',
  'aiScan.title': 'AI scan of this area (optional)',
  'aiScan.helpLabel': 'What does the AI scan do?',
  'aiScan.expand': 'Show all',
  'aiScan.collapse': 'Fold',
  'aiScan.help1': 'An AI scan means photographing the whole area once — walls, floor, ceiling, fixtures. Every photo is saved immediately, album first.',
  'aiScan.help3': 'The scan is optional. Skipping it, or AI being unavailable, never blocks the inspection, the closeout or the report.',
  'aiScan.noPhotos': 'No area photos yet.',
  'area.otherIssues': 'Other issues',
  'area.otherIssuesHint': 'Problems found outside the checklist — each is its own finding.',
  'area.otherIssueCta': '+ Other issue found',
  'unable.body': 'Choose why this item could not be inspected. A short note is optional.',
  'unable.note': 'Note',
  'unable.notePlaceholder': 'One sentence, if useful',
  'unable.confirm': 'Mark as Unable to Inspect',
  'unable.noReason': 'No reason chosen',
  'unable.change': 'Change reason',
  'unable.choose': 'Choose reason',
  'finding.stepEvidence': 'Evidence',
  'finding.stepObservation': 'Observation',
  'finding.stepRecommendation': 'Recommendation',
  'finding.recommendationHint': 'Needed before the report is exported. Name the type of professional who should address it.',
  'finding.reportSettings': 'Report settings',
  'finding.tradeHint': 'A defect finding names the responsible trade in the report.',
  'finding.structuralConcern': 'Structural concern (inspector-confirmed)',
  'finding.structuralConcernHint': 'Movement / displacement / significant cracking / deformation / suspected instability / load-bearing concern → refer to a Professional Engineer (PE). Leave off for ordinary surface deterioration or masonry repair.',
  'finding.tradeMissing': 'Trade not chosen',
  'closeout.title': 'Closeout',
  'closeout.open': 'Closeout check',
  'closeout.openWithGaps': 'Closeout · {n} to fix',
  'closeout.blockingCount.one': '{n} item blocks the report',
  'closeout.blockingCount.other': '{n} items block the report',
  'closeout.blockingTitle': 'Must be fixed before the report',
  'closeout.blockingMeta': 'Only what is genuinely unfinished',
  'closeout.advisoryTitle': 'Worth a look',
  'closeout.advisoryMeta': 'Does not block the report',
  'closeout.gapUninspected': 'No result yet',
  'closeout.gapUnableNoReason': 'Unable to Inspect without a reason',
  'closeout.gapFindingNoRecommendation': 'Finding without a recommendation',
  'closeout.gapFindingNoTrade': 'Finding without a responsible trade',
  'closeout.gapFindingNoPhoto': 'Finding without a photo',
  'closeout.fix': 'Fix',
  'closeout.readyTitle': 'Nothing left to fix',
  'closeout.readyBody': 'Every item has a result and every finding is complete. The final report can be generated.',
  'closeout.continueToReport': 'Continue to the report',
  'report.title': 'Home Inspection Report',
  'report.open': 'Report',
  'report.statusTitle': 'Report status',
  'report.languageBody': 'Chosen at the end. English: one English PDF. Chinese: the English client PDF plus a Chinese copy for you.',
  'report.variantClient': 'English client report',
  'report.variantSelf': 'Chinese copy for the inspector',
  'report.preview': 'Preview: {name}',
  'report.previewSubtitle': 'Same content as the PDF',
  'report.generate': 'Generate final report',
  'report.generating': 'Generating…',
  // (The Round-2 generation confirmation was replaced on 2026-09-09 by the Owner-approved strict
  //  confirmation `report.finalConfirm*` — approved draft §5.2, which also states the deletion.)
  'report.generatedToast.one': '{n} PDF generated',
  'report.generatedToast.other': '{n} PDFs generated',
  'report.generateFailed': 'The report could not be generated. Nothing in the inspection was lost: tap Generate final report again, and contact us if it keeps failing.',
  'report.previewFailed': 'The preview could not be opened. Please try again.',
  'report.reviewAreaLocation': 'Whole house ({area})',
  'finding.chineseInClientReport': 'The English client report prints this field as written — please write it in English before generating the report.',
  'report.lockedTitle': 'Final report generated — editing is closed',
  'report.lockedBody': 'The working data of this inspection is read-only. The generated PDFs are kept under Reports.',
  'report.filesTitle': 'Generated files',
  'report.filesEmpty': 'No PDF generated yet',
  'report.filesCount.one': '{n} PDF',
  'report.filesCount.other': '{n} PDFs',
  'report.pages.one': '{n} page',
  'report.pages.other': '{n} pages',
  'report.share': 'Share / Save',
  'report.shareFailed': 'Could not share the file: {detail}',
  'report.downloaded': 'Downloaded {name}',
  'report.fileMissing': 'The PDF file is missing on this device.',
  'reports.emptyTitle': 'No reports yet',
  'reports.emptyBody': 'Every inspection appears here with its report state.',
  'reports.readyToGenerate': 'Ready to generate',
  'report.selfCopy': 'Inspector copy — not for delivery',
  'report.propertyInformation': 'Property Information',
  'report.clientInformation': 'Client Information',
  'report.inspectionDetails': 'Inspection',
  'report.agreement': 'Inspection Agreement',
  'report.fee': 'Fee',
  'report.feeAmount': 'USD {amount}',
  'report.summary': 'Summary',
  'report.limitedScopeStatement': 'This report reflects a limited, non-invasive, visual inspection of the readily accessible areas and components on the inspection date. It is not technically exhaustive. Concealed or inaccessible conditions are outside its scope. An inspection for radon or pests, including wood-destroying insects, is not part of a New York home inspection: this home inspection is not a pest or termite inspection, and any visible evidence noted in this report is reported only as observed at the time of the inspection. Determining the presence or absence of mold or other hazardous substances, and engineering or architectural analysis, are not required by the New York Standards of Practice and not included in this inspection.',
  'report.scopeStatement': 'Scope of inspection',
  'report.sections': 'Areas & Systems',
  'report.systemsSummary': 'Systems summary — the same check across locations',
  'report.areaNote': 'Area notes',
  'report.noFindings': 'No findings recorded in this area.',
  'report.observation': 'Observation',
  'report.recommendation': 'Recommendation',
  'report.responsibleTrade': 'Responsible trade',
  'report.priority': 'Priority',
  'report.severity': 'Level',
  'report.defectSummary': 'Summary of Deficiencies',
  'report.defectSummaryNote': 'This summary is not the complete report. Read the entire report, including the observations, limitations and scope for each area.',
  'report.defectSummaryEmpty': 'No deficiencies were recorded in this inspection.',
  'report.defectSummaryCount': '{n}',
  'report.chineseGoverned': 'This Chinese version is the inspector’s own copy and is not delivered; if it differs from the English version, the English version governs.',
  'report.variantClientSummary': 'Chinese summary for the client',
  'report.clientSummaryCopy': 'Chinese summary · for the client’s reference',
  'report.clientSummaryGoverned': 'This Chinese summary is for reference only; the English report governs.',
  'report.clientSummaryDefectNote': 'This summary is not the complete report. Read the entire English report, including the observations, limitations and scope for each area.',
  'report.clientSummaryUnableNote': 'The full limitation for each item is in the matching area of the English report.',
  'report.clientSummaryToggle': 'Also make a Chinese summary for the client',
  'report.clientSummaryToggleHint': 'A separate PDF: the Summary of Deficiencies and the parts not inspected, in Chinese, marked “for reference only; the English report governs”.',
  'report.unableSeeArea': 'See the limitations listed in each area.',
  'severity.title': 'Defect level',
  'severity.safety': 'Safety / Immediate attention',
  'severity.repair': 'Repair / Further evaluation',
  'severity.maintenance': 'Maintenance',
  'severity.suggestedHint': 'Suggested from what this item checks; change it if needed.',
  'severity.chosenHint': 'Printed with your choice in the report and on the summary page.',
  'report.evidence': 'Evidence',
  'report.figure': 'Fig. {n}',
  'report.unableSection': 'Unable to Inspect — Limitations',
  'report.reason': 'Reason',
  'report.note': 'Note',
  'report.inspectorNotes': 'Inspector Notes',
  'report.terms': 'Scope of This Inspection',
  'report.generatedAt': 'Generated',
  'report.page': 'Page',
  'report.address': 'Address',
  'report.propertyType': 'Property type',
  'report.yearBuilt': 'Year built',
  'report.basement': 'Basement',
  'report.client': 'Client',
  'report.phone': 'Phone',
  'report.email': 'Email',
  'report.inspectionDate': 'Inspection date',
  'report.inspector': 'Inspector',
  'report.weather': 'Weather',
  'report.notRecorded': 'Not recorded',

  // ---- Owner Test Round 2 (2026-09-04): agreement · signatures · layout · real AI · pool · zoom ----
  'status.offline': 'Offline',
  'common.notSet': 'Not set',
  'common.retry': 'Retry',
  'sync.storageTitle': 'Where your data lives',
  'sync.storageBody': 'Inspections, checklist results, notes, findings and photo records are stored on this device in the app’s own storage. Nothing is uploaded automatically.',
  'sync.offlineBody': 'Without a connection everything keeps working and saving. Only signing in and AI analysis need a connection.',
  'sync.aiBody': 'AI analysis sends the photos you selected, their notes and the area note through our backend for that analysis only. Results are stored on this device.',
  'sync.headerRule': 'The header shows a status only when something needs attention: Offline, Pending Sync or Sync Failed.',
  'inspections.lastEdited': 'Last edited {time}',
  'setup.yearPlaceholder': 'e.g. 1998',
  'photo.addNote': 'Add note',
  'photo.noteSubtitle': 'Saved with the photo · sent to the AI with it',
  'photo.noteSave': 'Save note',
  'photo.noteHint': 'Notes help the AI and appear with the photo in the pool.',
  'photo.pinchHint': 'Pinch to zoom · double-tap to zoom in · swipe for the next photo',
  'photo.zoomIn': 'Zoom in',
  'photo.zoomOut': 'Zoom out',
  'photo.zoomReset': 'Fit to screen',
  'pool.allPhotos': 'All photos',
  'pool.byArea': 'By area',
  'pool.groupEmptyTitle': 'No photos here yet',
  'pool.groupEmptyBody': 'Photos taken in this area or linked to its items and findings appear here.',
  'pool.indexHint': 'Photos are grouped by the area they were taken in or linked to. One photo can belong to several areas.',
  'pool.unclassified': 'Unclassified',
  'pool.unclassifiedHint': 'Imported photos not yet linked to any area',
  'pool.unclassifiedEmptyBody': 'Every photo is linked to an area. Import more photos to sort them later.',
  'area.aiBusy': 'AI analysis is already running',
  'area.aiContinueManually': 'Continue manually',
  'area.aiFailed': 'Analysis did not complete',
  'area.aiNotConfigured': 'AI backend is not configured in this build',
  'area.aiNotSignedIn': 'Sign in to use AI analysis',
  'area.aiPhase.preparing': 'Preparing photos…',
  'area.aiPhase.uploading': 'Uploading… {pct}%',
  'area.aiPhaseHint': 'You can keep inspecting; photos and notes are already saved.',
  'area.aiReanalyze': 'Analyze everything again',
  'area.aiRedoTitle': 'Analyze everything again? This uses AI again',
  'area.aiRedoConfirm': 'Use AI again and analyze',
  'billing.addOnSheetTitle': '3-inspection pack',
  'billing.addOnSheetBody': 'No AI inspections left. The 3-inspection pack adds 3 more, which never expire.',
  'area.reportNote': 'Area note for the report',
  'area.reportNoteHint': 'Printed under this area in the report',
  'area.scanNote': 'Area note',
  'area.scanNoteHint': 'What you noticed overall — saved with the area and sent to the AI as context.',
  'area.scanNotePlaceholder': 'e.g. musty smell; cabinet under the sink was packed; water heater dated 2015…',
  'area.viewAllPhotos': 'View all',
  'ai.concern': 'Why it matters',
  'ai.question': 'AI asks',
  'ai.scopeNote': 'Scope note',
  'ai.ignore': 'Ignore',
  'ai.ignoredToast': 'Suggestion ignored',
  'ai.privacy': 'Photos, their notes and the area note are sent through our backend only when you tap analyze.',
  'ai.fail.network': 'No response from the server. Check the connection and retry.',
  'ai.fail.timeout': 'The analysis took too long. Retry with fewer photos.',
  'ai.fail.aborted': 'The analysis was cancelled.',
  'ai.fail.malformedResponse': 'The server returned an unreadable answer. Retry.',
  'ai.fail.unauthenticated': 'Sign in again to use AI analysis.',
  'ai.fail.badRequest': 'The request was rejected. Reopen the area and retry.',
  'ai.fail.tooLarge': 'The photos are too large to send. Retry with fewer photos.',
  'ai.fail.tooManyImages': 'Too many photos for one analysis. Select fewer.',
  'ai.fail.unsupportedImage': 'A photo format is not supported.',
  'ai.fail.rateLimited': 'AI is busy. Wait a moment and retry.',
  'ai.fail.providerError': 'The AI provider had a problem. Retry.',
  'ai.fail.providerRefused': 'The AI declined to analyze these photos.',
  'ai.fail.unavailable': 'AI analysis is unavailable right now.',
  'ai.fail.imageFailed': 'A photo could not be prepared for upload.',
  'ai.fail.interrupted': 'The previous analysis was interrupted before it finished.',
  'ai.fail.unknown': 'AI analysis failed.',
  // NY F1 Round 4 (Owner ruling OD-13, 2026-09-09). The scope-reminder card: an AI result about
  // something New York does not require is shown, never dropped — but it is never a defect, never a
  // task, never in the report draft and carries no confidence. Title, body and the two button
  // labels are the Owner-approved draft text (NY-AGREEMENT-V2-AND-REPORT-FLOW-APPROVED-DRAFT
  // -20260909.md §8.1), verbatim.
  'ai.scopeReminder.title': 'Outside the New York inspection scope',
  'ai.scopeReminder.body':
    'This is not required by the New York Standards of Practice and is not part of this inspection. If you want to look at it yourself, record what you see under Other Findings; it will print as a voluntary observation.',
  'ai.scopeReminder.observationLabel': 'What you saw',
  'ai.scopeReminder.savedToast': 'Recorded under Other Findings',
  // The limitation prompt: an obstruction never becomes work of its own — the concealed part of the
  // component that IS in scope becomes a printed limitation (PP-22).
  'ai.limitation.title': 'Part of this component was not observed',
  'ai.limitation.body': 'What is in the way is not inspected. Record the part that could not be observed on the component itself; the report prints it with the reason why.',
  'ai.limitation.record': 'Record the limitation',
  'ai.limitation.extentLabel': 'Which part could not be observed',
  'ai.limitation.savedToast': 'Limitation recorded',
  // Why the card says what it says. Basis wording per NY-REPORT-AUTHORITY.md §4.12: an item New
  // York merely does not require is never described as excluded by New York law.
  'ai.scopeNote.notRequiredNotIncluded': 'Not required by the New York Standards of Practice and not included in this inspection.',
  'ai.scopeNote.notPartOfNyHomeInspection': 'Not part of a New York home inspection.',
  'ai.scopeNote.clientExcluded': 'Excluded at the client’s request; the report describes the reduced scope.',
  'ai.scopeNote.uncertainSubject': 'The AI could not place this in a New York system, so it stays an observation.',
  'ai.scopeNote.presenceQuestion': 'Confirm whether the house has this before it becomes checklist work.',
  'ai.scopeNote.limitationPrompt': 'A component that is in scope was concealed — record it as a limitation.',
  'ai.scopeNote.pestObservationOnly': 'This home inspection is not a pest or termite inspection; an inspection by a licensed pest / termite professional is recommended.',
  'ai.scopeNote.sentenceRemoved': 'A sentence was removed: a home inspection does not state code compliance or similar conclusions.',
  'ai.fail.blockedNoScope': 'This agreement was signed without a recorded inspection scope (an earlier version). Sign the inspection agreement again to use AI analysis.',
  'ai.fail.blockedNoScopeState': 'AI analysis currently covers New York properties only: set the property state to New York (NY) in the inspection details.',
  'ai.fix.agreement': 'Sign the agreement',
  'ai.fix.property': 'Set the property state',
  // Final Review wording reminder (RP-02). A reminder, never a block.
  'wording.title': 'Wording to review',
  'wording.body': 'A New York home inspection does not state these. Nothing is blocked — change them if you agree.',
  'wording.reminderCount.one': '{n} wording reminder',
  'wording.reminderCount.other': '{n} wording reminders',
  'wording.source.areaName': 'Area name',
  'wording.source.itemTitle': 'Checklist row you added',
  'wording.source.photoCaption': 'Photo note',
  'wording.suggestion.P-01': 'Describe the condition and the safety concern; recommend correction by the responsible trade.',
  'wording.suggestion.P-02': 'Describe the evidence you saw; a home inspection does not determine legal use.',
  'wording.suggestion.P-03': 'Ownership and boundary location were not determined by this inspection.',
  'wording.suggestion.P-04': 'Value and purchase advice are outside a home inspection.',
  'wording.suggestion.P-05': 'Describe the movement or deterioration observed and recommend evaluation by a licensed professional engineer.',
  'wording.suggestion.P-06': 'Write “material of a type that may contain …; not determined by this inspection” and refer to the appropriate professional.',
  'wording.suggestion.P-07': 'Remaining life, causes, costs and methods of correction are outside a home inspection.',
  'wording.suggestion.P-08': 'Say what could not be observed and why; a concealed part is never described as satisfactory.',
  'wording.suggestion.P-09': 'Say what the photos show: “not visible in the photos provided; confirm on site”.',
  'wording.suggestion.P-10': 'Water quality and gas or carbon-monoxide testing are outside a home inspection.',
  'wording.suggestion.P-11': 'Describe the type and the rating label as observed; capacity and efficiency are not evaluated.',
  'wording.suggestion.P-12': 'A home inspection is not a warranty, a guarantee or a certification.',
  'wording.suggestion.P-13': 'The report describes the building, not the client or the transaction.',
  'wording.suggestion.P-14': 'The agreement’s legal clauses are printed by the app; the report does not restate them.',
  'wording.suggestion.P-15': 'Describe whether the window opened and any limitation; make no egress-compliance determination.',
  'wording.suggestion.P-16': 'A recirculating range hood is a configuration, not a deficiency; a deficiency comes only from an observed problem with a visible duct.',
  'layout.addMenu': 'Add to the layout',
  'layout.addFloor': 'Add a floor',
  'layout.addBasement': 'Add basement',
  'layout.addGarage': 'Add garage',
  'layout.addAttic': 'Add attic',
  'layout.addOther': 'Add other area',
  'layout.atticBody': 'Attic access and the visible structure, insulation and ventilation.',
  'layout.basementNotSet': 'Type not set',
  'layout.emptyHint': 'Nothing set yet. Add floors, basement, garage, attic or other areas — or use the suggestion for the property type.',
  'layout.finishNotSet': 'Finish not set',
  'layout.finishOptional': 'Finish (optional)',
  'layout.floorEditSubtitle': 'Rooms on this floor',
  'layout.garageAttached': 'Attached',
  'layout.garageDetached': 'Detached',
  'layout.otherName': 'Area name',
  'layout.otherPlaceholder': 'e.g. Pool house, Workshop',
  'layout.present': 'Included',
  'layout.removeFloor': 'Remove this floor',
  'layout.removeRow': 'Remove',
  'layout.rowEdit': 'Edit {name}',
  'layout.suggestedDetail': 'Preset for this property type; adjust anything afterwards.',
  'layout.useSuggestedFor': 'Use the suggestion for {type}',
  'report.variantAgreement': 'Inspection Agreement (PDF)',
  'report.variantAddendum': 'Addendum',
  'cloud.saved': 'In your account',
  'cloud.pending': 'Waiting to save to your account',
  'cloud.deviceOnly': 'On this device only (the client did not consent to account storage)',
  'cloud.noteOffline': 'You are offline. These PDFs are on this device and are saved to your account when you are back online.',
  'cloud.noteUnavailable': 'Account storage is not available right now. The PDFs are on this device and will be retried.',
  'cloud.noteNotConfigured': 'Account storage is not set up yet. The PDFs are kept on this device.',
  'cloud.listTitle': 'Reports in your account',
  'cloud.listMeta': 'Read-only, available on any device you sign in on',
  'cloud.listEmpty': 'No reports in your account yet. Final reports are saved here automatically.',
  'cloud.open': 'Open',
  'cloud.openFailed': 'This report could not be opened. Check the connection and try again.',
  'cloud.refresh': 'Refresh',
  'cloud.loading': 'Loading…',
  'cloud.loadingList': 'Reading the reports in your account…',
  'cloud.opening': 'Opening…',
  'addendum.cardTitle': 'Found an error after delivery?',
  'addendum.cardBody': 'The delivered report cannot be changed. Write a dated, signed addendum that says which item it corrects and why. It is kept in your account with the report and goes to the client with it.',
  'addendum.write': 'Write an addendum',
  'addendum.sheetTitle': 'Addendum',
  'addendum.sheetSubtitle': 'The original report stays as delivered',
  'addendum.languageHint': 'The client has the English report, so write the addendum in English.',
  'addendum.item': 'Item corrected',
  'addendum.itemPlaceholder': 'e.g. Kitchen · Sink, page 6',
  'addendum.reason': 'Reason',
  'addendum.reasonPlaceholder': 'e.g. The wrong photo was attached',
  'addendum.correction': 'Correction',
  'addendum.correctionPlaceholder': 'What the report should say for this item',
  'addendum.photos': 'Photos (optional, up to 4)',
  'addendum.addPhotos': 'Choose from library',
  'addendum.removePhoto': 'Remove photo {n}',
  'addendum.photoLimit': 'Up to 4 photos. The extra ones were not added.',
  'addendum.signature': 'Inspector signature',
  'addendum.clearSignature': 'Clear',
  'addendum.drawHere': 'Sign here',
  'addendum.required': 'Fill in this field',
  'addendum.signRequired': 'Sign first',
  'addendum.dateLine': 'Date: {date}',
  'addendum.create': 'Create the addendum PDF',
  'addendum.creating': 'Creating…',
  'addendum.created': 'Addendum {n} created. It is also being saved to your account.',
  'addendum.failed': 'The addendum could not be created. Please try again.',
  'addendum.print.title': 'Addendum to {title}',
  'addendum.print.number': 'Addendum No. {n}',
  'addendum.print.property': 'Property',
  'addendum.print.client': 'Client',
  'addendum.print.original': 'Original report',
  'addendum.print.originalLine': '{title}, issued {date}',
  'addendum.print.issued': 'Date of this addendum',
  'addendum.print.item': 'Item corrected',
  'addendum.print.reason': 'Reason',
  'addendum.print.correction': 'Correction',
  'addendum.print.photos': 'Photos',
  'addendum.print.photoCaption': 'Addendum photo {n}',
  'addendum.print.statement': 'This addendum is part of the report named above. The original report has not been altered. Except as stated in this addendum, the original report remains as issued.',
  'addendum.print.inspector': 'Inspector',
  'addendum.print.signature': 'Signature',
  'addendum.print.notRecorded': 'Not recorded',
  'report.agreementDocument': 'Agreement document',
  'report.agreementSigned': 'Signed by {client} (client) and {inspector} (inspector) on {date}',
  'report.agreementSignedLabel': 'Signed',
  'report.agreementNotSigned': 'Not signed',
  'report.agreementSeparate': 'The signed Inspection Agreement is exported as its own PDF; it is not part of the report.',
  'report.agreementUnsignedNote': 'Sign the Inspection Agreement under Inspection Details to export it as its own PDF.',
  'report.demoBanner': 'DEMO — not a paid inspection. This document is for demonstration only.',
  'report.demoWatermark': 'DEMO',
  'report.feeDemo': 'Demo (no fee)',
  'agreement.documentTitle': 'Inspection Agreement',
  'agreement.viewSubtitle': 'Fixed document · New York',
  'agreement.fixedNote': 'The agreement text is fixed. Only the property, client, inspector, date, fee and signatures change per inspection.',
  'agreement.languageLabel': 'Agreement language',
  'agreement.languageNote': 'The agreement comes in one language, chosen by you for the client: all Chinese or all English.',
  'agreement.languageBilingualOld': 'This agreement was signed under an earlier copy and is bilingual. Choosing a language re-opens it to be signed again under the current copy.',
  'agreement.feeLabel': 'Inspection fee (USD)',
  'agreement.feePlaceholder': 'e.g. 450',
  'agreement.orDemo': 'or',
  'agreement.reopenTitle': 'This change needs a new signature',
  'agreement.reopenBody': 'The agreement is signed. After this change it goes back to "not signed", and the client and the inspector sign it again.',
  'agreement.reopenConfirm': 'Change it and sign again',
  'agreement.demo': 'Demo inspection (no fee)',
  'agreement.demoHint': 'Demo inspections are recorded like any other; the agreement and the report carry a DEMO watermark.',
  'agreement.clientSignature': 'Client signature',
  'agreement.inspectorSignature': 'Inspector signature',
  'agreement.notSigned': 'Not signed',
  'agreement.signed': 'Signed',
  'agreement.signedOn': 'Signed {date}',
  'agreement.missing': 'Still needed: {list}',
  'agreement.req.fee': 'fee or Demo',
  'agreement.req.feeFirst': 'Enter the fee or choose Demo before signing.',
  'agreement.req.clientName': 'client name',
  'agreement.req.clientSignature': 'client signature',
  'agreement.req.clientAccepted': 'client acceptance',
  'agreement.req.inspectorName': 'inspector name',
  'agreement.req.inspectorSignature': 'inspector signature',
  'agreement.req.inspectorAccepted': 'inspector acceptance',
  'agreement.sign': 'Sign agreement',
  'agreement.resign': 'Sign again',
  'agreement.signTitle': 'Sign the agreement',
  'agreement.signBody': 'Both parties sign here, on this device. Signatures are saved with the inspection and printed on the agreement PDF.',
  'agreement.readBeforeSigning': 'Read the agreement',
  'agreement.signerName': 'Name',
  'agreement.signerNamePlaceholder': 'Full name as signed',
  'agreement.drawHere': 'Sign here',
  'agreement.clear': 'Clear',
  'agreement.acceptClient': 'I have read and agree to this Inspection Agreement',
  'agreement.acceptInspector': 'I confirm the inspection details and scope above',
  'agreement.completeSigning': 'Complete signing',
  'agreement.pdfShare': 'Share agreement PDF',
  'agreement.pdfShared': 'Agreement PDF ready',
  'agreement.pdfUnsigned': 'Sign the agreement before exporting it.',
  'agreement.pdfFailed': 'The agreement PDF could not be created. Please try again.',
  'agreement.docProperty': 'Property',
  'agreement.docClient': 'Client',
  'agreement.docInspector': 'Inspector',
  'agreement.docInspectionDate': 'Inspection date',
  'agreement.docFee': 'Fee',
  'agreement.docVersion': 'Document version',
  'agreement.feeDemo': 'Demo — no fee',
  'agreement.clauseNote': 'The following clauses are required by New York State and are reproduced verbatim.',
  'agreement.sectionDetails': 'Inspection details',
  'agreement.sectionScope': 'Scope of inspection',
  'agreement.sectionClauses': 'Required clauses',
  'agreement.sectionSignatures': 'Signatures',
  'agreement.languageVersion': 'English Version',
  'inspectionStage.draft': 'Draft',
  'inspectionStage.inProgress': 'In Progress',
  'inspectionStage.ready': 'Ready for Closeout',
  'inspectionStage.reportComplete': 'Report Complete',
  'agreement.paymentOutside': 'Payment happens outside the app — there is no invoice or online payment here.',
  'agreement.signedAtLabel': 'Signed',
  'common.yes': "Yes",
  'common.no': "No",
  'ai.fail.blockedRealClient': "Cloud AI analysis is not open for this inspection. On-site inspection, photos, notes and the checklist all work as usual.",
  'ai.fail.blockedLegalReview': "AI analysis is not open for this type of property. On-site inspection, photos, notes and the checklist all work as usual.",
  'ai.fail.blockedJurisdiction': "AI analysis opens once the licence and the property state are recorded for this inspection.",
  'gate.title': "Before continuing this inspection",
  'gate.viewOnly': "Everything already recorded stays here and can be viewed. Nothing is deleted.",
  'gate.licenceTitle': 'Link your license first',
  'gate.licenceBody': 'Your license is checked against New York’s public list and linked to the account once; it is then copied onto every new inspection.',
  'gate.openProfile': "Inspector Profile",
  'gate.stateTitle': "Which state is this property in?",
  'gate.stateBody': "Choose the state so the right inspection rules apply. This was never confirmed for this inspection.",
  'gate.unsupportedTitle': "This state is not open yet",
  'gate.unsupportedBody': "Full inspection support for {state} is not open yet, so the official inspection workflow cannot be used for this property.",
  'gate.mismatchTitle': "Licence and property are in different states",
  'gate.mismatchBody': "Your licence is recorded in {inspectorState} and this property is in {propertyState}. The official inspection workflow is not open for that combination.",
  'gate.occupancyTitle': "Has this property been previously occupied as a dwelling?",
  'gate.occupancyBody': "Asked once for this inspection. Answer from what you know about the property.",
  // (The new-construction block copy was withdrawn on 2026-09-09: OD-9 serves a newly built home as
  // a New Build visual observation instead of blocking it. The unit-count block keeps its own copy.)
  'gate.actionUnavailable': "The official inspection workflow is not open for this inspection yet.",
  'settings.profile': "Inspector Profile",
  'settings.profileBody': 'Display name, photo, linked license',
  'settings.report': 'Report branding',
  'settings.reportBody': 'Company, logo, contact, defect level names',
  'branding.companyTitle': 'Company and contact',
  'branding.companyBody': 'Printed on the report cover and in the header of every page; empty fields print nothing.',
  'branding.noLogo': 'No logo',
  'branding.logoPick': 'Choose logo',
  'branding.logoChange': 'Change logo',
  'branding.logoRemove': 'Remove',
  'branding.logoFailed': 'The logo could not be saved: {message}',
  'branding.companyName': 'Company name',
  'branding.companyNamePlaceholder': 'e.g. Long Island Home Inspection LLC',
  'branding.phone': 'Phone',
  'branding.email': 'E-mail',
  'branding.website': 'Website',
  'branding.websitePlaceholder': 'e.g. www.example.com',
  'branding.showInspectorPhoto': 'Print my photo in the report',
  'branding.showInspectorPhotoHint': 'Your photo is set in Inspector profile; it prints beside the inspection details.',
  'branding.tiersTitle': 'Defect level names',
  'branding.tiersBody': 'The three colours are fixed (red · orange · blue). You can rename the levels; leave a field empty to use the standard name. The English report uses the English names, the Chinese copy the Chinese names.',
  'branding.tierNamesEnglish': 'English report',
  'branding.tierNamesChinese': 'Chinese copy',
  'branding.saved': 'Saved. Applies to the next report you generate.',
  'profile.body': 'The display name and photo are yours to set; the license is linked to the account after New York’s list confirms it and is copied onto every new inspection. Later changes never rewrite an inspection that already carries it.',
  'profile.name': 'Display name',
  'profile.licensedState': "Licensed State",
  'profile.licenseNumber': "License Number",
  'licence.number': 'License number',
  'licence.numberPlaceholder': '11 digits, e.g. 16000012345',
  'licence.holder': 'License holder',
  'licence.expires': 'Valid until',
  'licence.firstName': 'First name (as on the license)',
  'licence.lastName': 'Last name (as on the license)',
  'licence.bindBody': 'Enter the number and name on your license. We check them against New York’s public list of licensed home inspectors. Once they match, the license number and holder name print on every report automatically and cannot be changed in the app.',
  'licence.bind': 'Check and link',
  'licence.binding': 'Checking…',
  'licence.notListed': 'New York’s list has no such license number. Check it against your license and try again.',
  'licence.expiredOnList': 'New York’s list shows this license as expired. An expired license cannot be linked.',
  'licence.taken': 'This license number is already linked to another account. One license links to one account only; contact support if this is wrong.',
  'licence.accountHasAnother': 'This account is already linked to another license number. Contact support to change it.',
  'licence.nameMismatch': 'The name does not match New York’s list: the last name must be the same and the first name must start with the same letter. Enter it as written on the license.',
  'licence.listUnavailable': 'New York’s list cannot be reached right now. Try again later.',
  'licence.offline': 'You are offline. The license can be checked once you are online.',
  'licence.unavailable': 'The server cannot be reached right now. Try again later.',
  'licence.deviceNotVerified': 'This device has not passed the e-mail check yet. Verify it first, then link the license.',
  'licence.lockedNote': 'The license is linked to this account and cannot be changed in the app. To change it, e-mail support@nezha2capital.com (subject starting with [验屋师]) with proof of the license.',
  'licence.expiredNote': 'The license has expired, so formal reports cannot be produced; Demo inspections are not affected. After renewing, check again below.',
  'licence.recheck': 'Check again',
  'licence.displayNameNote': 'This is the name shown in the app. Reports print the license holder’s name.',
  'report.licenceMissing': 'Link your license first: New York requires the license number on every report.',
  'report.licenceExpired': 'The license has expired, so a formal report cannot be produced. After renewing, check it again under Inspector Profile.',
  'device.title': 'Verify this device',
  'device.body': 'This is the first time this device signs in to this account. To keep an account from being shared, it needs a check: tap the button below and we send a 6-digit code to {email}. Enter it to continue.',
  'device.bodyNoEmail': 'This is the first time this device signs in to this account. An e-mail code is needed to continue.',
  'device.codeLabel': 'Code',
  'device.send': 'Send code',
  'device.resend': 'Send again',
  'device.sent': 'The code has been sent. Check your e-mail (and the spam folder).',
  'device.rateLimited': 'Too many codes requested. Wait a minute and try again.',
  'device.sendFailed': 'The code could not be sent. Try again later.',
  'device.verify': 'Verify',
  'device.invalid': 'That code is wrong or has expired. Enter it again or send a new one.',
  'device.verifyFailed': 'The device cannot be verified right now. Try again later.',
  'device.limitNote': 'An account can be signed in on at most 2 devices at once (for example a phone and a computer). Once this one is verified, the device used longest ago signs out.',
  'device.signOut': 'Sign out',
  'device.revoked': 'Your account signed in on another device (at most 2 at once), so this device was signed out. Signing in again needs an e-mail code.',
  'device.listTitle': 'Signed-in devices (at most 2)',
  'device.thisDevice': 'This device',
  'device.listNote': 'An account can be signed in on at most 2 devices at once; signing in on a third signs out the one used longest ago.',
  'profile.licenseNumberHint': "Enter it exactly as printed on your licence.",
  'profile.selectState': "Choose a state",
  'profile.licenseShape': "Enter the licence number as it appears on the licence.",
  'profile.status': "Licence Status",
  'profile.statusLicensed': 'Linked, current',
  'profile.statusMissing': 'Not linked or expired',
  'profile.saved': "Inspector profile saved",
  'setup.statePlaceholder': "Select",
  'setup.needState': "Choose the state the property is in.",
  'setup.needOccupancy': "Answer whether the property has been previously occupied.",
  'setup.needLicence': 'Link your license first.',
  'setup.fillLicence': "Add licence",
  'setup.stillMissing': "Can't create yet — missing: {list}",
  'setup.missingAddress': "property address",
  'setup.missingLayout': "house layout",
  'setup.missingStart': "how to start",
  'setup.stateNotOpen': "Full inspection support for {state} is not open yet.",
  // ---- NY F1 Round 2 · mandatory descriptors (OD-1 = A1; NY-REPORT-AUTHORITY §8 / §9) ----
  'descriptor.roofMaterialCondition': 'Roof material and condition',
  'descriptor.roofObservationMethod': 'Method used to observe the roof',
  'descriptor.waterSupplyPipingMaterial': 'Water supply piping material',
  'descriptor.dwvPipingMaterial': 'Drain, waste and vent piping material',
  'descriptor.waterHeaterCapacity': 'Water heater capacity',
  'descriptor.waterHeaterEnergySource': 'Water heater energy source',
  'descriptor.mainWaterShutoffLocation': 'Main water shut-off location',
  'descriptor.mainFuelShutoffLocation': 'Main fuel shut-off location',
  'descriptor.heatingFuel': 'Heating fuel',
  'descriptor.waterSupplySource': 'Water supply',
  'descriptor.wasteDisposalSource': 'Waste disposal',
  'descriptor.serviceAmperageVoltage': 'Service amperage and voltage',
  'descriptor.disconnectLocations': 'Main disconnect and sub-panel locations',
  'descriptor.aluminumBranchWiring': 'Aluminum branch wiring',
  'descriptor.smokeDetectorPresence': 'Smoke detectors',
  'descriptor.coDetectorPresence': 'Carbon monoxide detectors',
  'descriptor.branchConductorCondition': 'Visible branch conductors',
  'descriptor.heatingEquipmentType': 'Heating equipment',
  'descriptor.heatingDistribution': 'Heating distribution',
  'descriptor.heatSourceEachHabitableSpace': 'Heat source in each habitable space',
  'descriptor.coolingEquipmentType': 'Cooling equipment',
  'descriptor.coolingDistribution': 'Cooling distribution',
  'descriptor.interiorSurfaces': 'Walls, ceilings and floors',
  'descriptor.insulationUnfinishedSpaces': 'Insulation in unfinished spaces',
  'descriptor.ventilationAtticFoundation': 'Ventilation of attic and foundation areas',
  'descriptor.atticObservationMethod': 'Attic method of observation',
  'descriptor.atticConditionsObserved': 'Attic conditions observed',
  'descriptorChoice.roofObservationMethod.walked': 'Walked the roof',
  'descriptorChoice.roofObservationMethod.ladderAtEave': 'From a ladder at the eave',
  'descriptorChoice.roofObservationMethod.groundZoom': 'From the ground (zoom)',
  'descriptorChoice.roofObservationMethod.drone': 'Drone',
  'descriptorChoice.roofObservationMethod.notObserved': 'Not observed',
  'descriptorChoice.waterHeaterEnergySource.gas': 'Gas',
  'descriptorChoice.waterHeaterEnergySource.oil': 'Oil',
  'descriptorChoice.waterHeaterEnergySource.electric': 'Electric',
  'descriptorChoice.waterHeaterEnergySource.propane': 'Propane',
  'descriptorChoice.waterHeaterEnergySource.other': 'Other',
  'descriptorChoice.heatingFuel.gas': 'Gas',
  'descriptorChoice.heatingFuel.oil': 'Oil',
  'descriptorChoice.heatingFuel.electric': 'Electric',
  'descriptorChoice.heatingFuel.propane': 'Propane',
  'descriptorChoice.heatingFuel.other': 'Other',
  'descriptorChoice.heatingFuel.unknown': 'Unknown',
  'descriptorChoice.waterSupplySource.public': 'Public',
  'descriptorChoice.waterSupplySource.private': 'Private',
  'descriptorChoice.waterSupplySource.unknown': 'Unknown',
  'descriptorChoice.wasteDisposalSource.public': 'Public',
  'descriptorChoice.wasteDisposalSource.private': 'Private',
  'descriptorChoice.wasteDisposalSource.unknown': 'Unknown',
  'descriptorChoice.aluminumBranchWiring.present': 'Present',
  'descriptorChoice.aluminumBranchWiring.notObserved': 'Not observed',
  'descriptorChoice.smokeDetectorPresence.present': 'Present',
  'descriptorChoice.smokeDetectorPresence.absent': 'Absent',
  'descriptorChoice.smokeDetectorPresence.partial': 'Partial',
  'descriptorChoice.coDetectorPresence.present': 'Present',
  'descriptorChoice.coDetectorPresence.absent': 'Absent',
  'descriptorChoice.coDetectorPresence.partial': 'Partial',
  'descriptorChoice.heatingDistribution.forcedAir': 'Forced air',
  'descriptorChoice.heatingDistribution.hydronic': 'Hot water (hydronic)',
  'descriptorChoice.heatingDistribution.steam': 'Steam',
  'descriptorChoice.heatingDistribution.radiant': 'Radiant',
  'descriptorChoice.heatingDistribution.baseboard': 'Electric baseboard',
  'descriptorChoice.heatingDistribution.other': 'Other',
  'descriptorChoice.heatSourceEachHabitableSpace.allSpaces': 'All habitable spaces',
  'descriptorChoice.heatSourceEachHabitableSpace.someSpaces': 'Some spaces only',
  'descriptorChoice.heatSourceEachHabitableSpace.none': 'None',
  'descriptorChoice.coolingDistribution.ducted': 'Ducted',
  'descriptorChoice.coolingDistribution.ductless': 'Ductless split',
  'descriptorChoice.coolingDistribution.roomUnits': 'Room units',
  'descriptorChoice.coolingDistribution.other': 'Other',
  'descriptorChoice.atticObservationMethod.entered': 'Entered the attic',
  'descriptorChoice.atticObservationMethod.fromHatch': 'From the hatch',
  'descriptorChoice.atticObservationMethod.notObserved': 'Not observed',
  'descriptor.sectionTitle': 'Report descriptors',
  'descriptor.sectionHint': 'Printed in the report even when everything is normal. One tap each.',
  'descriptor.needed': 'Needed for the final report — not recorded yet.',
  'descriptor.textPlaceholder': 'One short line',
  'descriptor.detailPlaceholder': 'Where / which rooms (optional)',
  'descriptor.prefilled': 'Prefilled from the heating fuel — change it if the water heater differs.',
  'descriptor.safetyPrompt': 'Absent detectors are a safety concern. Record a safety finding.',
  'descriptor.recordSafetyFinding': 'Record safety finding',
  'descriptor.safetyFindingTitle': 'Smoke / carbon monoxide detector absent',
  'facts.title': 'Property facts',
  'facts.hint': 'Asked once. Printed in the report.',
  'readiness.title': 'Required report descriptors',
  'report.statusCloseoutClear': 'The closeout check passed; the descriptors below are still needed before the final report.',
  'readiness.ready': 'Every mandatory descriptor is recorded. The final report can be generated.',
  'readiness.blocked.one': '{n} descriptor still needed before the final report',
  'readiness.blocked.other': '{n} descriptors still needed before the final report',
  'readiness.propertyFact': 'Property fact',
  'readiness.fix': 'Record',
  'readiness.licenceWarning': 'No licence number is recorded for this inspector. The report prints without it.',
  'readiness.notApplicable': 'Report descriptors are not required for a Demo inspection.',
  'report.description': 'Description',
  'report.observedComponents': 'Observed Components',
  'report.observed': 'Observed',
  'report.observedDefect': 'Observed — defect',
  'report.notObserved': 'Not observed →',
  'report.notPresentSection': 'Not present / not installed',
  'report.notPresentWording': 'Not present / not installed at the time of inspection',
  'report.licensedInspector': 'New York State Licensed Home Inspector',
  'report.licenseNumber': 'License #',
  'report.waterSupply': 'Water supply',
  'report.wasteDisposal': 'Waste disposal',
  'report.heatingFuel': 'Heating fuel',

  // ---- NY F1 Round 3: parts not observed (OD-5 = A) · Unable reasons (OD-7 = C) · carport (AG-01 product half) ----
  'unable.scopeWhole': 'Whole item not inspected',
  'unable.scopePartial': 'Part of it not seen',
  'unable.portionTitle': 'Part not observed',
  'unable.partialBody': 'Choose why this part could not be seen. The result already recorded stays for the part that was observed.',
  'unable.partialNeedsResult': 'The observed part still needs its own result (Pass / Defect).',
  'unable.extent': 'Which part',
  'unable.extentPlaceholder': 'e.g. south slope under the solar array · north wall behind stored items',
  'unable.reasonWords': 'Reason in your own words',
  'unable.otherNeedsReason': '“Other” needs the actual reason in your own words.',
  'unable.reportReason': 'The report will state',
  'unable.obstruction': 'What obstructs this part',
  'unable.obstructionGeneric': 'Stored items, coverings or finishes (general wording)',
  'unable.obstructionSolar': 'Solar panels / solar array',
  'unable.partialUnavailable': '“Part of it not seen” needs an observed part with its own result (Pass / Defect) first. This item is currently Unable to Inspect or N/A as a whole.',
  'unable.confirmPortion': 'Record part not observed',
  'unable.replacesPortions.one': 'Replaces {n} recorded part not observed — the whole item will be recorded as not observed.',
  'unable.replacesPortions.other': 'Replaces {n} recorded parts not observed — the whole item will be recorded as not observed.',
  'item.unableNeedsNote': 'Reason “Other” needs the actual reason in your own words.',
  'item.limitedPortions': 'Parts not observed',
  'item.limitedPortionsHint': 'The observed part keeps its result. Each part recorded here prints as a limitation with its reason.',
  'item.addLimitedPortion': 'Record part not observed',
  'item.portionRemoveTitle': 'Remove the parts not observed?',
  'item.portionRemoveBody.one': 'This item has {n} recorded part not observed. Changing the whole item to {result} will remove that record. Continue?',
  'item.portionRemoveBody.other': 'This item has {n} recorded parts not observed. Changing the whole item to {result} will remove those records. Continue?',
  'item.portionRemoveConfirm': 'Remove and mark {result}',
  'item.portionUnnamed': 'part not named',
  'item.portionEdit': 'Edit',
  'item.portionRemove': 'Remove',
  'item.portionNeedsNote': 'A part with reason “Other” needs the actual reason in your own words.',
  'area.limitedPortionsMeta.one': '{n} part not observed',
  'area.limitedPortionsMeta.other': '{n} parts not observed',
  'closeout.gapUnableNoNote': 'Unable to Inspect (Other) without a written reason',
  'closeout.gapPortionNoNote': 'Part not observed (Other) without a written reason',
  'report.portion': 'Part not observed',
  'report.portionUnspecified': 'a part of this component',
  'report.observedPartial': 'part not observed →',
  'report.supplementalRow': 'supplemental observation (beyond the New York minimum)',
  'space.carport': 'Carport',
  'layout.garageCarport': 'Carport',

  // ---- NY F1 Round 5 · Owner rulings 2026-09-09 (OD-8 … OD-13) + Owner default X-1 (2026-09-11) ----
  // The report-flow copy is the approved draft NY-AGREEMENT-V2-AND-REPORT-FLOW-APPROVED-DRAFT-20260909
  // §5.1 / §5.2 / §5.3 and the privacy paragraph §6, word for word. The gate, unit-count, consent and
  // voluntary-observation lines are product copy written for this round and listed for Owner review.
  'service.homeInspection': 'Home inspection',
  'service.newBuild': 'New Build',
  'gate.agreementTitle': 'Sign the inspection agreement first',
  'gate.agreementBody': 'Field work starts once the client and you have signed the agreement for this inspection. Everything already recorded stays here.',
  'gate.openAgreement': 'Open the agreement',
  'gate.unitCountTitle': 'More than four units is not supported',
  'gate.unitCountBody': 'The official inspection workflow covers buildings with one to four dwelling units. Change the unit count below if it was recorded by mistake.',
  'setup.unitCount': 'Dwelling Units',
  'setup.unitCountHint': 'How many dwelling units the building has.',
  'setup.unitCountOverLimit': '5 or more',
  'setup.unitCountBlocked': 'The official inspection workflow does not cover buildings with more than four dwelling units.',
  'setup.agreementLater': 'You can create the inspection now and sign the agreement later; field work starts after both signatures.',
  'setup.countTrialTitle': 'This uses one of your free inspections',
  'setup.countSingleTitle': 'This uses one single purchase',
  'setup.countBody': 'Once it is created it counts as used. If you delete it before its report and it never used AI, the use is given back; giving back has its own limit of {limit} times every 30 days. Once AI has run or the report exists, it is not given back. Make sure you can carry this inspection out now.',
  'setup.countRemaining': '{n} left afterwards.',
  'setup.countTrialTotal': 'Each account has {n} free inspections in total; they do not renew monthly.',
  'setup.missingState': 'property state',
  'setup.missingOccupancy': 'previous occupancy',
  'setup.clientPhonePlaceholder': 'e.g. 516-555-0123',
  'setup.clientEmailPlaceholder': 'e.g. name@example.com',
  'setup.inspectorFromLicence': 'The inspector name comes from the licence bound to your account; it is the name the agreement and the report print.',
  'setup.countConfirm': 'Yes, start the inspection',
  'setup.newBuildHint': 'Answer No for a newly built or never-occupied home: it is served as a New Build visual observation, with its own agreement and report.',
  'agreement.aiConsentTitle': 'AI-assisted analysis and cloud storage',
  'agreement.aiConsentToggle': 'The client consents (optional)',
  'agreement.aiConsentOptional': 'Optional, ticked by the client when signing. Without consent the inspection and the report are exactly the same; AI-assisted analysis is not used and the report stays on this device only.',
  'agreement.aiConsentBySigning': 'Ticked by the client when signing',
  'agreement.aiConsentRowLabel': 'AI & cloud storage',
  'agreement.aiConsentGivenOn': 'The client consented on {date}',
  'agreement.esignTick': 'I agree to sign this Agreement electronically and to receive this Agreement and the report electronically (paper is available at any time)',
  'agreement.aiTick': 'I agree to AI-assisted analysis of the photos and to the final report being stored in the inspector’s cloud account',
  'agreement.consentsNote': 'Every box is ticked by the client. The full clauses are under “Read the agreement” above.',
  'agreement.liabilityTick': 'I agree to the limitation of liability and claims terms (liability limited to the fee, notice before repair, one year to sue, report for my use only)',
  // Remote signing through a one-time link (Owner decision list 2026-10-19 items 4 / 45).
  'agreement.signedRemotely': 'Signed remotely through a one-time link; received {date} from IP address {ip}.',
  'agreement.signedRemotelyNoIp': 'Signed remotely through a one-time link; received {date}.',
  'remote.title': 'Sign remotely by link',
  'remote.subtitle': 'For a client who is not here: they sign on their own phone or computer.',
  'remote.step1': 'Send the link. The client opens it — no account needed — reads the whole agreement, ticks the consents, types their name and signs.',
  'remote.step2': 'Back here, tap “Check whether the client signed”. Their signature comes into this agreement and the copy on our server is deleted.',
  'remote.step3': 'Then you sign on this device and the agreement is complete. The link works for 14 days and can be signed once.',
  'remote.changeNote': 'Record the fee (or Demo) and any additional services first. If you change the fee, services, date, address or names after sending, the client’s signature no longer matches and a new link is needed.',
  'remote.notReady': 'Record the fee or turn on Demo, and complete the additional services, before sending a link.',
  'remote.noWebAddress': 'This build does not know the web version’s address, so it cannot make a link.',
  'remote.offline': 'Offline — connect to the network and try again.',
  'remote.notSignedIn': 'Sign in again, then try again.',
  'remote.unavailable': 'Remote signing is not available right now. Try again later, or sign in person.',
  'remote.waiting': 'The client has not signed yet.',
  'remote.taken': 'The client has signed. Now sign yourself to complete the agreement.',
  'remote.expired': 'This link has expired. Make a new one.',
  'remote.changed': 'The agreement changed after the link was sent (fee, services, date, address or names), so the client signed an older version. That signature was not used and the link was cancelled — make a new link.',
  'remote.gone': 'This link no longer exists. Make a new one.',
  'remote.copied': 'Link copied.',
  'remote.copyByHand': 'Copying is not allowed here — select the link above and copy it.',
  'remote.shareText': 'Please read and sign the home inspection agreement for {property}:',
  'remote.validUntil': 'Works until {date}.',
  'remote.copy': 'Copy link',
  'remote.share': 'Share',
  'remote.check': 'Check whether the client signed',
  'remote.checking': 'Checking…',
  'remote.discard': 'Cancel this link',
  'remote.create': 'Make the link',
  'remote.creating': 'Making the link…',
  'remote.signNow': 'Sign now',
  'remote.cardWaiting': 'Remote signing link sent — waiting for the client (works until {date}).',
  'remote.cardClientSigned': 'The client signed remotely. Your signature completes the agreement.',
  'remote.openLink': 'Remote signing link',
  'remote.sendLink': 'Send a link to sign remotely',
  'remote.inPersonInstead': 'Sign in person instead',
  'remote.signedAt': 'Signed remotely, received {date}',
  'remote.esignGiven': 'Consent to electronic records and signatures ticked {date}',
  'remote.liabilityGiven': 'Limitation of liability and claims terms accepted {date}',
  'remote.liabilityMissing': 'The limitation of liability and claims terms were not accepted: sign this one in person',
  'remote.aiGiven': 'Consent to cloud storage and AI-assisted analysis ticked {date}',
  'remote.aiNotGiven': 'No consent to cloud storage and AI-assisted analysis',
  'remotePage.title': 'Home inspection agreement',
  'remotePage.intro': 'Your home inspector sent you this agreement to sign. Read the whole document below, then tick, type your name and sign at the bottom.',
  'remotePage.documentTitle': 'The agreement',
  'remotePage.consentsNote': 'You tick every box yourself. The full clauses are in the agreement above.',
  'remotePage.needEsign': 'Tick the consent to electronic records and signatures to sign here (or ask your inspector for a paper copy).',
  'remotePage.needLiability': 'Tick your agreement to the limitation of liability and claims terms to sign this agreement.',
  'remotePage.needName': 'Type your name.',
  'remotePage.needSignature': 'Sign in the box with your finger or mouse.',
  'remotePage.needAccept': 'Tick that you have read and agree.',
  'remotePage.submit': 'Sign the agreement',
  'remotePage.sending': 'Sending…',
  'remotePage.done': 'Signed. Thank you.',
  'remotePage.doneNext': 'Your inspector signs next and then sends you a PDF copy of the agreement signed by both of you. You can close this page.',
  'remotePage.alreadySigned': 'This agreement has already been signed. Your inspector will send you the signed copy.',
  'remotePage.expired': 'This link has expired. Ask your inspector for a new one.',
  'remotePage.notFound': 'This link is not valid. Ask your inspector for a new one.',
  'remotePage.unavailable': 'The agreement could not be opened right now. Please try again later.',
  'remotePage.offline': 'No connection — check the network and tap Sign again.',
  'remotePage.privacy': 'No account is needed. Your name, signature and choices go only to your inspector; once they have taken them, the copy on our server is deleted.',
  'agreement.tickedAt': 'Ticked by the Client: {date}',
  'agreement.req.clientEsignConsent': 'the client’s consent to electronic signing',
  'agreement.req.clientLiabilityAck': 'the client’s agreement to the limitation of liability',
  'agreement.req.additionalServices': 'additional service name and cost',
  'agreement.req.servicesFirst': 'Complete the name and cost of each additional service before signing.',
  'agreement.serviceName': 'Additional service',
  'agreement.serviceNamePlaceholder': 'e.g. Shed beyond the garage',
  'agreement.serviceFee': 'Cost (USD)',
  'agreement.serviceAdd': 'Add an additional service',
  'agreement.serviceRemove': 'Remove',
  'agreement.servicesNone': 'No additional services',
  'agreement.aiConsentGiven': 'Consented',
  'agreement.aiConsentNone': 'No consent',
  'agreement.aiConsentWithdraw': 'Record written withdrawal',
  'agreement.consentWithdrawnOn': 'Consent withdrawn in writing on {date}',
  'ai.fail.blockedAgreementUnsigned': 'AI analysis opens once the inspection agreement is signed. On-site inspection, photos, notes and the checklist all work as usual.',
  'ai.fail.blockedNotConsented': 'The client did not consent to AI-assisted analysis for this inspection, or withdrew that consent. Everything else works as usual.',
  'ai.fail.blockedAgreementOutdated': 'This inspection was signed on an earlier agreement without the AI consent clause. Have the client sign the current agreement to use AI analysis.',
  'report.previewed': 'Previewed ✓',
  'report.agreementPrepared': 'Made at signing ✓ no preview needed',
  'report.agreementNoPreview': 'No preview needed',
  'report.agreementDrawing': 'Making the PDF',
  'report.agreementFailed': 'The PDF could not be made at signing',
  'agreement.pdfKeptFailed': 'The agreement PDF could not be made. Tap Retry to make it again; generating the final report also makes it again.',
  'report.outputGenerated': 'Generated ✓',
  'report.previewFirst':'Preview each report before generating. The final PDF cannot be changed afterwards.',
  'report.previewPending.one': '{n} report still to preview',
  'report.previewPending.other': '{n} reports still to preview',
  'report.finalConfirmTitle': 'Generate the final report? This cannot be undone.',
  'report.finalConfirmBody': "After generation this inspection is final: no result, finding, photo, note or limitation can be edited, added or removed; a correction can only be issued as a separate addendum. All working data of this inspection — photos held in the app, notes, findings and AI suggestions — is deleted from this device and from our servers, and only the final PDF report(s) and the signed Agreement PDF are kept. If the client consented to cloud storage, these PDFs are also kept in your account. Photos taken with the camera stay in your device's photo library. The report is due to the client within five business days after the inspection.",
  'report.finalConfirmCheck': 'I have previewed the report and understand it cannot be changed.',
  'report.finalConfirmAction': 'Generate and delete working data',
  'report.purgedBanner': 'Final report generated. Working data deleted; only the PDF files remain.',
  'reports.generated': 'Report generated',
  'report.otherObservations': 'Other observations (not required by the New York Standards of Practice)',
  'finding.voluntaryQuick': 'Looked — no issue observed',
  'finding.voluntaryTag': "Not required by NY SOP · observed at inspector's discretion",
  'finding.voluntaryHint': 'Recorded as a courtesy observation: it prints under Other observations and is not part of the inspection scope.',
  'finding.voluntarySaved': 'Recorded as a voluntary observation',
  'finding.voluntaryTitlePlaceholder': 'What you looked at (e.g. lawn irrigation)',
  'reportDue.newBuild': 'Report due by {date} — the agreement provides for delivery within five business days after the observation.',
  'reportDue.newBuildOverdue': 'Report overdue since {date} — the agreement provides for delivery within five business days after the observation.',
  'inspections.deletePurgedBody': 'Only the PDF files are left for this inspection: the final report and the signed agreement. Deleting it removes them from this device. This cannot be undone.',
  'settings.privacyNotice': "Your inspections are stored on this device. Photos, notes and checklist context are sent to our backend (Supabase, on AWS in the United States) only when you tap AI analysis, and only to relay them to Anthropic for analysis; our backend stores no photos and its logs contain none. Anthropic does not train on your content and does not retain it in the ordinary course beyond 30 days. No client name or address is sent. Generating a final report deletes the inspection's working data from the app and keeps only the PDFs. Your sign-in e-mail and password are protected by industry-standard hashing and encryption in transit; you can delete your account at any time in Settings.",
  // ---- Advanced AI review · AI jobs · notifications (Owner contract 2026-09-13; copy frozen in §56) ----
  'ai.disclaimer': 'AI can make mistakes. All analysis results are for reference only.',
  'advanced.title': 'Advanced AI Deep Review',
  'advanced.start': 'Start deep review',
  'advanced.starting': 'Starting…',
  'advanced.processing': 'Advanced AI analyzing · keep inspecting',
  'advanced.completeLine': 'Advanced AI review complete',
  'advanced.completeToast': 'Advanced AI review complete · {subject}',
  'advanced.viewResult': 'View result',
  'advanced.busyHint': 'The AI service is busy right now; the system retries by itself. You can keep inspecting.',
  'advanced.status.pendingUpload': 'Waiting to upload',
  'advanced.status.submitting': 'Submitting…',
  'advanced.status.notSubmitted': 'Not submitted',
  'advanced.status.queued': 'Queued',
  'advanced.status.processing': 'Analyzing',
  'advanced.status.retrying': 'Recovering',
  'advanced.status.completed': 'Completed',
  'advanced.status.inputInvalid': 'This content cannot be analyzed',
  'advanced.status.providerUnavailable': 'AI service temporarily unavailable',
  'advanced.status.failedFinal': 'Analysis did not complete',
  'advanced.status.interrupted': 'Interrupted · recovering',
  'aiTasks.title': 'AI Tasks',
  'aiTasks.subtitleAll': 'Advanced AI reviews on this device',
  'aiTasks.group.active': 'Analyzing',
  'aiTasks.group.unread': 'Completed · not viewed',
  'aiTasks.group.viewed': 'Viewed',
  'aiTasks.group.failed': 'Failed · needs attention',
  'aiTasks.counts': '{active} analyzing · {unread} not viewed',
  'push.explainTitle': 'Allow notifications',
  'push.explainBody': 'With notifications allowed, you are told when an advanced AI review is complete even while you are inspecting another area.',
  'push.allow': 'Allow notifications',
  'push.later': 'Not now',
  'setup.fixBeforeCreate': 'Please complete: {list}',
  'setup.fieldAddressRequired': 'Enter the property address',
  'setup.fieldLayoutRequired': 'Add at least one area to inspect',
  'setup.fieldStartRequired': 'Choose how to start',
  'item.blockGuidance': '⑥ Inspection guidance',
  'item.blockPhotos': '③ Linked photos',
  'item.blockAi': '④ AI observations and possible issues',
  'item.blockResult': '① Inspection result',
  'item.blockPortions': '⑤ Portions not observed',
  'item.blockWording': '② AI report wording',
  'item.guidanceNone': 'No additional guidance for this item.',
  'ai.itemHasObservation': 'AI observation',
  'ai.observation': 'AI observed',
  'ai.toVerify': 'AI to verify',
  'ai.noneForRow': 'No AI observation for this item yet.',
  'ai.evidenceNote': 'AI observations are supporting evidence; the result is yours to choose.',
  'ai.confidence.high': 'Confidence: high',
  'ai.confidence.medium': 'Confidence: medium',
  'ai.confidence.low': 'Confidence: low',
  'ai.verdict.noVisibleDefect': 'AI found nothing ≠ no defect. Confirm it yourself.',
  'ai.verdict.fieldTestRequired': 'Requires on-site testing; photos cannot settle it. Operate it, then choose the result.',
  'ai.suggestedEvidenceTitle': 'Photos placed by AI',
  'ai.suggestedEvidenceHint': 'They count as this item’s evidence and reach the report. Tap “Not this item” on a misplaced one.',
  'ai.confirmEvidence': 'Confirm',
  'ai.dismissEvidence': 'Not this item',
  'ai.evidenceConfirmedToast': 'Added to linked photos',
  'ai.suggestedEvidenceCount.one': '{n} photo placed by AI',
  'ai.suggestedEvidenceCount.other': '{n} photos placed by AI',
  'pool.pendingLink': 'Placed by AI · {target}',
  'ai.clearDefectTitle': 'Mark as no defect?',
  'ai.clearDefectBody': 'The AI saw “{headline}” on this item. Marked as no defect, the report states no deficiency and this AI observation stays out of it.',
  'ai.clearDefectConfirm': 'Mark no defect',
  'ai.unclassifiedTitle': 'AI found {n} unclassified',
  'ai.unclassifiedHint': 'What the AI could not match to an existing checklist item. File it under an item, record it as an other issue, or ignore it.',
  'ai.fileToItem': 'File under an existing item',
  'ai.filePickTitle': 'Which item?',
  'ai.filedToast': 'Filed under “{name}”',
  'ai.notFilable': 'This observation is outside the New York inspection scope and cannot be filed under a checklist item. Record it as an other issue, or ignore it.',
  'ai.recordAsOther': 'Record as other issue',
  'ai.needsOnline': 'Requires a network connection',
  'area.aiOffline': 'Requires a network connection',
  'area.aiStatusLine': 'Photos analyzed: {photos} · items with AI observations: {rows} · unclassified: {unclassified}',
  'aiScan.help2': 'When you tap AI analysis, the photos, their notes and the area note go to the AI, which matches what it sees to the checklist as AI observations on each item. It never sets a result: you choose the result on each item.',
  'advanced.entry': 'Ask the advanced AI',
  'advanced.explainer': 'A deep review can take longer and may use more of your AI allowance. You can keep inspecting other areas while it runs in the background.',
  'advanced.inputGuidance': 'the inspection guidance',
  'advanced.inputObservations': 'the AI observations',
  'advanced.inputNote': 'the inspector note',
  'advanced.deeper': 'for a deeper review.',
  'advanced.adjustPhotos': 'View / adjust photos',
  'advanced.photosLabel': 'Photos to review',
  'advanced.noPhotos': 'Link photos in ③ first: the advanced AI only reads the photos linked to this item.',
  'advanced.resultLabel': 'Advanced AI review',
  'advanced.reviewedAt': 'Reviewed · {time}',
  'advanced.confirmed': 'Can be confirmed',
  'advanced.possible': 'What it might be',
  'advanced.unconfirmed': 'Cannot be confirmed from the photos',
  'advanced.onSite': 'Check on site',
  'advanced.morePhotos': 'Photo needed',
  'advanced.chatEntry': 'Ask the advanced AI more',
  'advanced.again': 'Deep review again',
  'advanced.offlineHint': 'Requires a network connection',
  'chat.title': 'Advanced AI conversation',
  'chat.subtitle': 'About this item only',
  'chat.placeholder': 'e.g. What else should I check on site?',
  'chat.send': 'Send',
  'chat.usageHint': 'Each question uses more of your AI allowance.',
  'chat.notRecord': 'The conversation is not a formal record; only what you add to the inspector note becomes a fact of the report.',
  'chat.answering': 'The advanced AI is answering…',
  'chat.addToNote': 'Add to inspector note',
  'chat.addToNoteHint': 'Edit if needed; saving adds it to the inspector note.',
  'chat.addedToast': 'Added to the inspector note',
  'chat.you': 'You',
  'chat.ai': 'Advanced AI',
  'chat.failed': 'This question could not be sent: {reason}',
  'wording.en': 'Report wording (English — what the client reads)',
  'wording.zh': 'Chinese reference',
  'wording.zhEditable': 'Report wording (Chinese)',
  'wording.zhStale': 'The Chinese is not in line with the English you edited yet (it only affects the Chinese copy you keep). “AI: rewrite from my words” below brings it in line.',
  'wording.enStale': 'The English is not in line with the Chinese you edited yet — and the client report is the English one. Use “AI: rewrite from my words” below, or edit the English yourself; the final report cannot be generated until it is in line.',
  'wording.trade': 'Responsible professional',
  'wording.tradeNone': '(none)',
  'wording.write': 'Have the AI write this',
  'wording.rewrite': 'AI rewrite',
  'wording.writing': 'The AI is writing…',
  'wording.regenerateFromMine': 'AI: rewrite from my words',
  'wording.regenerateHint': 'The AI keeps the facts and the meaning of what you wrote, turns it into report language and brings both languages in line.',
  'wording.regenerateTitle': 'Have the AI rewrite the wording from what you wrote?',
  'wording.regenerateBody': 'The AI keeps the facts and the meaning of what you wrote, turns it into report language and brings both languages in line. Your own text is replaced by the new version.',
  'wording.fromObservation': 'Write the wording from the AI observation',
  'wording.fromObservationHint': 'The AI writes the observation above into this item’s report wording (block ② above). The result you choose still decides.',
  'wording.fromObservationNeedsResult': 'Choose a result in block ① above first, then write the wording.',
  'wording.overwriteTitle': 'Replace your edited wording with an AI rewrite?',
  'wording.overwriteBody': 'The wording you wrote will be replaced by the AI’s new version.',
  'wording.editHint': 'Edit the English directly; your words are what the report prints.',
  'wording.editHintZh': 'Edit the Chinese directly. Then use “AI: rewrite from my words” below and the English follows in report language.',
  'wording.factsChanged': 'The facts changed; check this wording.',
  'wording.source.ai': 'Written by the AI',
  'wording.source.inspector': 'Edited by you',
  'wording.source.library': 'Library sentence (not yet written by the AI)',
  'wording.pendingStatus': 'The wording appears once a result is chosen.',
  'closeout.gapDefectNoFacts': 'Defect without an inspector note, an AI observation or report wording',
  'closeout.gapDefectNoWording': 'Defect whose report wording is not written yet',
  'closeout.gapWordingEnglishNotSynced': 'English wording not in line with the Chinese you edited',
  'closeout.gapClientEnglishChinese': 'Chinese would print in the English client report: rewrite it in English',
  'closeout.gapClientEnglishChineseText': '{source}: “{text}”',
  'closeout.inspectionLevel': 'Inspection details',
  'closeout.chineseTitle': 'Chinese in the English client report',
  'closeout.chineseMeta': 'The client’s English report prints these as written. Rewrite them in English before the report.',
  'closeout.aiNote': 'AI observations are supporting evidence and never block the closeout. Every result is the inspector’s own.',
  // Before you leave (2026-10-27, AI overnight review + workflow review A2 / B3): reminders only, never a block.
  'closeout.leaveTitle': 'Before you leave',
  'closeout.leaveMeta.one': '{n} item · does not block the report',
  'closeout.leaveMeta.other': '{n} items · do not block the report',
  'closeout.leaveBody': 'Take the photo or check it now, while you are still on site.',
  'closeout.leaveCount': '{n} more worth a look',
  'closeout.leaveShowAll': 'Show {n} more',
  'closeout.leaveAiFieldTest': 'AI: needs an on-site test; a photo cannot tell',
  'closeout.leaveAiUnclear': 'AI could not tell from the photos',
  'closeout.leaveAiLowDefect': 'AI leans Defect, with low confidence',
  'closeout.leaveAiLow': 'AI has low confidence',
  'closeout.leaveRowOpen': 'No result yet',
  'closeout.leaveRowPass': 'You chose Pass; add a close-up or a note',
  'closeout.leaveRowNoTest': 'You chose Pass, but no test is recorded',
  'closeout.leaveAiAsks': 'AI asks: {text}',
  'closeout.leaveAdvancedAsks': 'Advanced AI asks for a photo: {text}',
  'closeout.leaveAiQuestionTitle': 'AI question',
  'closeout.leaveUnfiled.one': '{n} AI finding still to file',
  'closeout.leaveUnfiled.other': '{n} AI findings still to file',
  'closeout.leaveUnfiledBody': 'On the area page: file into an item, record as another finding, or ignore',
  'closeout.leaveNewPhotos.one': '{n} new photo the AI has not seen',
  'closeout.leaveNewPhotos.other': '{n} new photos the AI has not seen',
  'closeout.leaveNewPhotosBody': 'Run the area analysis again before you leave',
  'closeout.leaveAlarmTitle': 'Smoke / CO alarms · {level}',
  'closeout.leaveAlarmBody': 'No alarm item on this level is marked Pass or Defect yet',
  'closeout.leaveNoPhoto': 'Has a result but no photo',
  'closeout.leaveNoPhotoDefect': 'Defect with no photo',
  'aiTasks.empty': 'No advanced AI reviews yet. Start one from an item with “Ask the advanced AI”.',
  'findings.defectRow': 'Checklist defect',
  'findings.emptyBody': 'Items marked Defect and the other findings you record appear here.',
  // Owner device feedback 2026-09-14: report cover photo · inspector photo
  'cover.label': 'Report cover',
  'cover.add': 'Cover photo',
  'cover.fieldTitle': 'Report cover photo',
  'cover.fieldBody': 'A photo of the front of the house, printed on the report cover and shown on the inspection card and in the Reports list.',
  'cover.pendingNote': 'Saved with the record once the inspection is created.',
  'cover.options': 'Report cover photo',
  'cover.view': 'View cover',
  'cover.replace': 'Replace cover',
  'cover.remove': 'Remove cover',
  'cover.removeTitle': 'Remove the cover photo?',
  'cover.removeBody': 'Removes the cover from the app only; the copy in the system photo album is untouched.',
  'cover.viewer': 'Report cover',
  'cover.locked': 'The report is generated; the cover can no longer be changed.',
  'cover.saveFailed': 'The cover photo could not be saved: {message}',
  'profile.photo': 'Inspector photo',
  'profile.photoBody': 'Shown as the avatar at the top right of the app. Use a front-facing photo where the face is clearly visible.',
  'profile.photoRemove': 'Remove photo',
  'profile.photoRemoveTitle': 'Remove the inspector photo?',
  'profile.photoRemoveBody': 'The top-right avatar returns to the name initials. The copy in the system photo album is untouched.',
  'profile.photoFailed': 'The photo could not be saved: {message}',
  // Owner-selected designs 2026-09-14 (Figma 191:3 / 191:7 / 191:10): New Inspection as a four-step
  // form, the Sign Agreement sheet with the inspection facts above the signatures, address completion.
  'setup.next': 'Next',
  'setup.stepOf': 'Step {n} / {total} · {title}',
  'setup.step1Progress': 'Property',
  'setup.draftRestoredTitle': 'Picked up where you left off',
  'setup.draftRestoredBody': 'What you had entered — cover photo included — is back. Carry on, or start over.',
  'setup.draftDiscard': 'Start over',
  'setup.step2Title': 'Client · Time · Inspector',
  'setup.step4Title': 'Agreement · Start',
  'setup.step1Sub': 'First, what kind of house this is',
  'setup.step2Sub': 'Only the people and the time for this inspection',
  'setup.step3Sub': 'Confirm the areas that will be created',
  'setup.step4Sub': 'Signing can wait; the start mode cannot',
  'setup.localityRow': 'City · State · ZIP',
  'setup.localityTitle': 'City, state and ZIP',
  'setup.coverSummary': 'Cover photo',
  'setup.missingJump': 'Tap to go to what is missing',
  'agreement.signedBoth': 'Both parties have signed',
  'address.suggestions': 'Address suggestions',
  'address.poweredBy': 'Powered by Google',
  'common.edit': 'Edit',
  'plan.areasCount.one': '{n} area',
  'plan.areasCount.other': '{n} areas',
  // Owner 2026-10-20: an unfinished inspection lives on ONE device; it moves by file; deleting an unused one gives back what it took.
  'inspectionStage.movedOut': 'Moved out',
  'setup.deviceNote': 'This inspection is finished on this device. To carry on somewhere else, use “Move to another device” in the Inspections list to export a file, then import it on the other device.',
  'inspections.importFile': 'Import an inspection from a file',
  'inspections.moveOut': 'Move to another device (export a file)',
  'inspections.movedSection': 'Moved to another device',
  'inspections.movedLine': 'Exported {date} · view only here',
  'inspections.deleteReleaseNote': 'No report has been generated for it yet: if it never used AI, deleting it gives back the free report or single purchase it took (at most {limit} every 30 days; {left} left now). One that used AI gives nothing back.',
  'inspections.deleteMovedBody': 'This is the copy left behind when the inspection moved to another device. Deleting it removes only this copy; the inspection on the other device is not affected, and nothing is given back.',
  'release.released.trial': 'Deleted. One free report was given back.',
  'release.released.single': 'Deleted. One single purchase was given back.',
  'release.kept.aiUsed': 'Deleted. It used AI, so what it took is not given back.',
  'release.kept.limitReached': 'Deleted. {limit} were already given back in the last 30 days, so this one is not.',
  'release.queued': 'Deleted. Whether what it took comes back is confirmed once you are online.',
  'moved.title': 'This inspection moved to another device',
  'moved.body': 'It was exported on {date} into the file “{file}”. So that two devices never change it apart, the copy on this device can only be viewed now. Import that file on the other device to carry on.',
  'moved.takenBy': '{device} imported it and is carrying on with it.',
  'moved.waiting': 'No other device has imported it yet.',
  'moved.undo': 'Undo the move and carry on here',
  'moved.undoTitle': 'Carry on with it on this device?',
  'moved.undoBody': 'Only while no other device has imported the file. Afterwards, delete the exported file and do not import it anywhere.',
  'moved.undoRefused': '{device} has already imported it. Carry on there; to bring it back, export it again on that device.',
  'moved.undone': 'You can carry on with this inspection here again.',
  'moved.deleteCopy': 'Delete the copy on this device',
  'move.title': 'Move to another device',
  'move.lockNote': 'After the export, the copy on this device can only be viewed. Moving never uses another free report or purchase.',
  'move.privacyNote': 'The file holds your client’s details and photos: delete it once it has been imported.',
  'move.confirm': 'Export the file',
  'move.working': 'Packing the file…',
  'move.done': 'Exported “{file}”. Import it on the other device to carry on.',
  'move.cancelled': 'Nothing was exported; the inspection stays on this device.',
  'move.failed': 'The export failed: {detail}',
  'move.blocked.aiRunning': 'AI is analysing this inspection. Export it once that has finished.',
  'move.blocked.other': 'This inspection cannot be exported right now.',
  'move.filePrefix': 'inspection-move',
  'importFile.working': 'Importing…',
  'importFile.problem.notAFile': 'This is not an inspection file exported by AI Home Inspector.',
  'importFile.problem.damaged': 'The file is incomplete or damaged (it may not have finished copying, or it was unzipped and zipped again). Export it again on the original device.',
  'importFile.problem.newerFormat': 'This file was exported by a newer version of the app. Update the app on this device first.',
  'importFile.problem.otherAccount': 'This file was exported from another account. Sign in to that account to import it.',
  'importFile.problem.alreadyHere': 'This inspection is already on this device and can be worked on here; there is nothing to import.',
  'importFile.problem.finished': 'The report of this inspection has already been generated; it cannot be imported.',
  'importFile.problem.notSignedIn': 'Sign in before importing.',
  'importFile.problem.storageFull': 'This device is out of storage space. Free some space and try again.',
  'importFile.problem.failed': 'The import failed: {detail}',
  'otherDevices.title.one': '{n} unfinished inspection on another device',
  'otherDevices.title.other': '{n} unfinished inspections on another device',
  'otherDevices.body': '{list}. Carry on where it was started — or, to work on it here, export it there with “Move to another device” and import the file here.',
  'otherDevices.item': '{device}, started {date}',
  'otherDevices.someDevice': 'another device',
  'settings.helpStep7': 'Changing devices: an unfinished inspection stays on the device it was started on. Your account, what you have left and the delivered reports are the same everywhere. To carry on elsewhere, use “Move to another device” in the Inspections list to export a file, then “Import an inspection from a file” on the other device.',
} as const;

export type MessageKey = keyof typeof enUS;
